import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MailCheck, MailX } from "lucide-react";

import { ApiError, api } from "../../api/client";
import CycleForm from "../../components/CycleForm";
import PsiberTab from "./PsiberTab";
import EmployeeSearch from "../../components/EmployeeSearch";
import Toast from "../../components/Toast";
import { useAuth, useMyRegion } from "../../context/AuthContext";
import { useFeatures } from "../../context/FeaturesContext";
import { useTabParam } from "../../hooks/useTabParam";
import {
  useDropdownLists,
  invalidateDropdownCache,
} from "../../hooks/useDropdownOptions";
import type { DropdownListItem, DropdownOptionItem } from "../../hooks/useDropdownOptions";
import type {
  AccessLevel,
  CompanyValue,
  Department,
  DocumentPermission,
  LeaveType,
  PublicHoliday,
  Region,
  TaxConfig,
} from "../../types";

function fmtPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

const REGION_CURRENCY: Record<string, { currency: string; locale: string }> = {
  uk: { currency: "GBP", locale: "en-GB" },
  za: { currency: "ZAR", locale: "en-ZA" },
  es: { currency: "EUR", locale: "es-ES" },
  us: { currency: "USD", locale: "en-US" },
};

function fmtCurrency(value: number, regionCode: string): string {
  const cfg = REGION_CURRENCY[regionCode] ?? REGION_CURRENCY.uk;
  return value.toLocaleString(cfg.locale, {
    style: "currency",
    currency: cfg.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const SETTINGS_TABS = [
  "email",
  "holidays",
  "tax",
  "permissions",
  "leave-types",
  "self-service",
  "field-options",
  "directory",
  "custom-fields",
  "notifications",
  "approvals",
  "surveys",
  "calendar",
  "calendar-colours",
  "recognition",
  "reviews",
  "review-actions",
  "features",
  "payroll",
  "system",
] as const;


interface EmailSettings {
  enabled: boolean;
  host: string;
  port: number;
  from_address: string;
  from_name: string;
  use_auth: boolean;
  username: string;
  has_password: boolean;
  use_tls: boolean;
  source: "environment" | "database";
  env_defaults: {
    host: string;
    port: number;
    from_address: string;
    from_name: string;
    enabled: boolean;
  };
  /** Pre-launch kill switch (NOTIFICATIONS_PAUSED): overrides `enabled`. */
  notifications_paused?: boolean;
  /** Addresses that still get real email while paused. */
  test_recipients?: string[];
}

/** Shown at the top of Settings > Email and the Send Log while all email is paused. */
function EmailPausedBanner() {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <MailX size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-amber-700" />
      <div>
        <p className="text-sm font-semibold text-amber-900">{t("emailConfig.pausedTitle")}</p>
        <p className="mt-1 text-xs text-amber-800">{t("emailConfig.pausedBody")}</p>
      </div>
    </div>
  );
}

/** Settings > Email: addresses that still get real email while all email is paused. */
function TestRecipientsCard({ initial, onSaved }: { initial: string[]; onSaved?: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial.join(", "));
  const [saved, setSaved] = useState<string[]>(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.put<{ test_recipients?: string[] }>("/api/settings/email", {
        test_recipients: value,
      });
      const list = res.test_recipients ?? [];
      setSaved(list);
      setValue(list.join(", "));
      setMessage({ ok: true, text: t("emailConfig.testRecipientsSaved") });
      onSaved?.();
    } catch (err) {
      setMessage({ ok: false, text: err instanceof ApiError ? err.message : "Failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex gap-3">
        <MailCheck size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-semibold text-gray-900">{t("emailConfig.testRecipientsTitle")}</p>
          <p className="text-xs text-gray-500">{t("emailConfig.testRecipientsBody")}</p>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("emailConfig.testRecipientsPlaceholder")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
            aria-label={t("emailConfig.testRecipientsTitle")}
          />
          {saved.length === 0 && (
            <p className="text-xs text-gray-500">{t("emailConfig.testRecipientsNone")}</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            >
              {t("emailConfig.testRecipientsSave")}
            </button>
            {message && (
              <span className={`text-xs ${message.ok ? "text-green-700" : "text-red-700"}`}>{message.text}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Permission keys used by the Roles & Permissions tab
 * ──────────────────────────────────────────────────────────────────────────── */

const PERMISSION_KEYS = [
  "can_manage_employees",
  "can_view_team",
  "can_manage_leave",
  "can_approve_leave",
  "can_view_compensation",
  "can_edit_compensation",
  "can_view_bank_details",
  "can_edit_bank_details",
  "can_view_bonuses",
  "can_edit_bonuses",
  "can_manage_deductions",
  "can_view_lateral_compensation",
  "can_view_all_compensation",
  "can_manage_payroll",
  "can_manage_training",
  "can_manage_certifications",
  "can_view_documents",
  "can_manage_performance",
  "can_start_reviews",
  "can_manage_expenses",
  "can_approve_expenses",
  "can_manage_settings",
  "can_manage_announcements",
  "can_manage_roles",
  "can_view_reports",
  "can_manage_onboarding",
] as const;

type PermKey = (typeof PERMISSION_KEYS)[number];

const PERMISSION_LABELS: Record<PermKey, string> = {
  can_manage_employees: "Manage employees",
  can_view_team: "View team",
  can_manage_leave: "Manage leave",
  can_approve_leave: "Approve leave",
  can_view_compensation: "View compensation",
  can_edit_compensation: "Edit compensation",
  can_view_bank_details: "View bank details",
  can_edit_bank_details: "Edit bank details",
  can_view_bonuses: "View bonuses & commissions",
  can_edit_bonuses: "Edit bonuses & commissions",
  can_manage_deductions: "Manage deductions & benefits",
  can_view_lateral_compensation: "View lateral compensation",
  can_view_all_compensation: "View all compensation (override)",
  can_manage_payroll: "Manage payroll",
  can_manage_training: "Manage training",
  can_manage_certifications: "Manage certifications",
  can_view_documents: "View documents",
  can_manage_performance: "Manage performance",
  can_start_reviews: "Start reviews",
  can_manage_expenses: "Manage expenses",
  can_approve_expenses: "Approve expenses",
  can_manage_settings: "Manage settings",
  can_manage_announcements: "Manage announcements",
  can_manage_roles: "Manage roles",
  can_view_reports: "View reports",
  can_manage_onboarding: "Manage onboarding",
};

const PERMISSION_GROUPS: { label: string; keys: PermKey[] }[] = [
  { label: "Employee Management", keys: ["can_manage_employees", "can_view_team"] },
  { label: "Leave & Attendance", keys: ["can_manage_leave", "can_approve_leave"] },
  { label: "Compensation & Payroll", keys: ["can_view_compensation", "can_edit_compensation", "can_view_bank_details", "can_edit_bank_details", "can_view_bonuses", "can_edit_bonuses", "can_manage_deductions", "can_view_lateral_compensation", "can_view_all_compensation", "can_manage_payroll"] },
  { label: "Training & Compliance", keys: ["can_manage_training", "can_manage_certifications"] },
  { label: "Documents", keys: ["can_view_documents"] },
  { label: "Performance", keys: ["can_manage_performance", "can_start_reviews"] },
  { label: "Expenses", keys: ["can_manage_expenses", "can_approve_expenses"] },
  { label: "Administration", keys: ["can_manage_settings", "can_manage_announcements", "can_manage_roles", "can_view_reports", "can_manage_onboarding"] },
];

/* ────────────────────────────────────────────────────────────────────────────
 * Self-Service field configuration types
 * ──────────────────────────────────────────────────────────────────────────── */

type SelfServiceAccessLevel = "hidden" | "view_only" | "direct_edit" | "requires_approval";

interface SelfServiceField {
  field_name: string;
  field_label: string;
  field_group: string;
  access_level: SelfServiceAccessLevel;
}

const FIELD_GROUPS_ORDER = [
  "Personal",
  "Address",
  "Contact",
  "Emergency",
  "Employment",
  "Compensation",
  "System",
];

interface PendingChangeRequest {
  id: number;
  field_name: string;
  field_label: string;
  current_value: string | null;
  requested_value: string | null;
  reason: string | null;
  status: string;
  employee_id: number;
  employee_name: string;
  created_at: string;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Notification categories and items
 * ──────────────────────────────────────────────────────────────────────────── */


/* ────────────────────────────────────────────────────────────────────────────
 * Main component
 * ──────────────────────────────────────────────────────────────────────────── */

export default function SettingsPage() {
  const [tab, setTab] = useTabParam(SETTINGS_TABS, "email");
  const { isSuperAdmin } = useAuth();

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500">System configuration.</p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-gray-200">
        <TabBtn active={tab === "email"} onClick={() => setTab("email")}>
          Email
        </TabBtn>
        <TabBtn active={tab === "holidays"} onClick={() => setTab("holidays")}>
          Public Holidays
        </TabBtn>
        <TabBtn active={tab === "tax"} onClick={() => setTab("tax")}>
          Tax Brackets
        </TabBtn>
        <TabBtn active={tab === "permissions"} onClick={() => setTab("permissions")}>
          Access Levels
        </TabBtn>
        <TabBtn active={tab === "leave-types"} onClick={() => setTab("leave-types")}>
          Leave Types
        </TabBtn>
        <TabBtn active={tab === "self-service"} onClick={() => setTab("self-service")}>
          Self-Service
        </TabBtn>
        <TabBtn active={tab === "field-options"} onClick={() => setTab("field-options")}>
          Field Options
        </TabBtn>
        <TabBtn active={tab === "directory"} onClick={() => setTab("directory")}>
          Directory
        </TabBtn>
        <TabBtn active={tab === "custom-fields"} onClick={() => setTab("custom-fields")}>
          Custom Fields
        </TabBtn>
        <TabBtn active={tab === "notifications"} onClick={() => setTab("notifications")}>
          Notifications
        </TabBtn>
        <TabBtn active={tab === "approvals"} onClick={() => setTab("approvals")}>
          Approvals
        </TabBtn>
        <TabBtn active={tab === "surveys"} onClick={() => setTab("surveys")}>
          Surveys
        </TabBtn>
        <TabBtn active={tab === "calendar"} onClick={() => setTab("calendar")}>
          Calendar
        </TabBtn>
        <TabBtn active={tab === "calendar-colours"} onClick={() => setTab("calendar-colours")}>
          Calendar Colours
        </TabBtn>
        <TabBtn active={tab === "recognition"} onClick={() => setTab("recognition")}>
          Recognition
        </TabBtn>
        <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")}>
          Reviews
        </TabBtn>
        <TabBtn active={tab === "review-actions"} onClick={() => setTab("review-actions")}>
          Review Actions
        </TabBtn>
        <TabBtn active={tab === "features"} onClick={() => setTab("features")}>
          Features
        </TabBtn>
        {isSuperAdmin && (
          <TabBtn active={tab === "payroll"} onClick={() => setTab("payroll")}>
            Payroll (PSIber)
          </TabBtn>
        )}
        <TabBtn active={tab === "system"} onClick={() => setTab("system")}>
          System
        </TabBtn>
      </nav>

      {tab === "email" && <EmailTab />}
      {tab === "holidays" && <HolidaysTab />}
      {tab === "tax" && <TaxBracketsTab />}
      {tab === "permissions" && <PermissionsTab />}
      {tab === "leave-types" && <LeaveTypesTab />}
      {tab === "self-service" && <SelfServiceTab />}
      {tab === "field-options" && <FieldOptionsTab />}
      {tab === "directory" && <DirectoryTab />}
      {tab === "custom-fields" && <CustomFieldsTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "approvals" && <ApprovalsTab />}
      {tab === "surveys" && <SurveysTab />}
      {tab === "calendar" && <CalendarFeedsTab />}
      {tab === "calendar-colours" && <CalendarColoursTab />}
      {tab === "recognition" && <RecognitionValuesTab />}
      {tab === "reviews" && <ReviewCyclesTab />}
      {tab === "review-actions" && <ReviewActionsTab />}
      {tab === "features" && <FeatureTogglesTab />}
      {tab === "payroll" && isSuperAdmin && <PsiberTab />}
      {tab === "system" && <SystemTab />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 text-sm font-semibold",
        active
          ? "border-b-2 border-pme-blue text-pme-blue"
          : "text-gray-500",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 1: Email
 * ──────────────────────────────────────────────────────────────────────────── */

function EmailTab() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);

  const [formEnabled, setFormEnabled] = useState(true);
  const [formHost, setFormHost] = useState("");
  const [formPort, setFormPort] = useState("");
  const [formFromAddress, setFormFromAddress] = useState("");
  const [formFromName, setFormFromName] = useState("");
  const [formUseAuth, setFormUseAuth] = useState(false);
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formUseTls, setFormUseTls] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const s = await api.get<EmailSettings>("/api/settings/email");
      setSettings(s);
      setFormEnabled(s.enabled);
      setFormHost(s.host || "");
      setFormPort(s.port ? String(s.port) : "");
      setFormFromAddress(s.from_address || "");
      setFormFromName(s.from_name || "");
      setFormUseAuth(s.use_auth);
      setFormUsername(s.username || "");
      setFormPassword(s.has_password ? "••••••••" : "");
      setFormUseTls(s.use_tls);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put("/api/settings/email", {
        enabled: formEnabled,
        host: formHost || null,
        port: formPort ? Number(formPort) : null,
        from_address: formFromAddress || null,
        from_name: formFromName || null,
        use_auth: formUseAuth,
        username: formUsername || null,
        password: formPassword || null,
        use_tls: formUseTls,
      });
      setNotice("Email settings saved.");
      await loadSettings();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setNotice(null);
    setError(null);
    setTestSending(true);
    try {
      const res = await api.post<{ sent_to: string }>(
        "/api/settings/email/test",
        testEmail.trim() ? { to: testEmail.trim() } : {},
      );
      setNotice(`Test email sent to ${res.sent_to}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setTestSending(false);
    }
  };

  if (!settings && !error) return <p className="text-sm text-gray-500">Loading...</p>;

  const env = settings?.env_defaults;

  return (
    <div className="space-y-5">
      {settings?.notifications_paused && <EmailPausedBanner />}
      {settings?.notifications_paused && (
        <TestRecipientsCard initial={settings.test_recipients ?? []} onSaved={() => void loadSettings()} />
      )}
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {notice && (
        <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>
      )}

      {settings?.source === "environment" ? (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-sm font-semibold text-gray-800">Using the server's email settings</p>
          <p className="mt-1 text-xs text-gray-500">
            The connection details below are the server defaults. You only need to change them to
            override the server; press Save to store an override.
          </p>
        </div>
      ) : settings ? (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-sm font-semibold text-gray-800">Using saved email settings</p>
          <p className="mt-1 text-xs text-gray-500">
            Blank fields below fall back to the server defaults.
          </p>
        </div>
      ) : null}

<EmailSendLog paused={!!settings?.notifications_paused} />

      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-5">
        <p className="text-sm text-gray-500">
          These settings override the server environment variables. Leave blank to use the server defaults.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={formEnabled}
            onChange={(e) => setFormEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
          />
          <span className="text-gray-700">SMTP Enabled</span>
        </label>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">SMTP Host</span>
            <input
              type="text"
              value={formHost}
              onChange={(e) => setFormHost(e.target.value)}
              placeholder={env?.host || "smtp.example.com"}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">SMTP Port</span>
            <input
              type="number"
              value={formPort}
              onChange={(e) => setFormPort(e.target.value)}
              placeholder={env?.port ? String(env.port) : "25"}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">From Address</span>
            <input
              type="email"
              value={formFromAddress}
              onChange={(e) => setFormFromAddress(e.target.value)}
              placeholder={env?.from_address || "noreply@example.com"}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">From Name</span>
            <input
              type="text"
              value={formFromName}
              onChange={(e) => setFormFromName(e.target.value)}
              placeholder={env?.from_name || "PME People"}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
            />
          </label>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formUseAuth}
              onChange={(e) => setFormUseAuth(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
            />
            <span className="text-gray-700">Use Authentication</span>
          </label>

          {formUseAuth && (
            <div className="grid grid-cols-1 gap-4 pl-6 md:grid-cols-2">
              <label className="block text-sm">
                <span className="block text-xs font-medium text-gray-700">Username</span>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="block text-xs font-medium text-gray-700">Password</span>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={formUseTls}
            onChange={(e) => setFormUseTls(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
          />
          <span className="text-gray-700">Use TLS</span>
        </label>

        <div className="flex gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={saveSettings}
            disabled={saving}
            className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Send Test Email</h4>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 text-sm">
            <span className="block text-xs font-medium text-gray-700">
              Recipient
            </span>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address for test..."
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={test}
            disabled={testSending}
            className="rounded-lg border border-pme-blue px-4 py-2 text-sm font-semibold text-pme-blue disabled:opacity-60"
          >
            {testSending ? "Sending..." : "Send Test"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 font-medium text-gray-900">{value}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 2: Public Holidays
 * ──────────────────────────────────────────────────────────────────────────── */

function HolidaysTab() {
  const { regionId: myRegionId } = useMyRegion();
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionId, setRegionId] = useState<string>("");
  const [year, setYear] = useState<string>(String(new Date().getFullYear()));
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const rs = await api.get<Region[]>("/api/regions");
        setRegions(rs);
        if (rs.length && !regionId) {
          const preferred = myRegionId ? rs.find((r) => r.id === myRegionId) : null;
          setRegionId(String(preferred?.id ?? rs[0].id));
        }
      } catch (err) {
        setError((err as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!regionId) return;
    const params = new URLSearchParams({ region_id: regionId, year });
    try {
      setHolidays(
        await api.get<PublicHoliday[]>(
          `/api/leave/public-holidays?${params.toString()}`,
        ),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }, [regionId, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const addHoliday = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!regionId || !newDate || !newName) return;
    try {
      await api.post("/api/leave/public-holidays", {
        region_id: Number(regionId),
        date: newDate,
        name: newName,
        is_observed: true,
      });
      setNewDate("");
      setNewName("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  const removeHoliday = async (id: number) => {
    if (!confirm("Delete this public holiday?")) return;
    await api.delete(`/api/leave/public-holidays/${id}`);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-700">Region</span>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-700">Year</span>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="mt-1 w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Observed</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {holidays.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                  No holidays for this region / year.
                </td>
              </tr>
            )}
            {holidays.map((h) => (
              <tr key={h.id}>
                <td className="px-3 py-2 font-mono tabular-nums">{h.date}</td>
                <td className="px-3 py-2">{h.name}</td>
                <td className="px-3 py-2">{h.is_observed ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => void removeHoliday(h.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={addHoliday}
        className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4"
      >
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-700">Date</span>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            required
            className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="flex-1 text-sm">
          <span className="block text-xs font-medium text-gray-700">Name</span>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          Add Holiday
        </button>
      </form>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 3: Tax Brackets
 * ──────────────────────────────────────────────────────────────────────────── */

function TaxBracketsTab() {
  const { regionId: myRegionId } = useMyRegion();
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionId, setRegionId] = useState<string>("");
  const [subRegion, setSubRegion] = useState<string>("");
  const [subRegions, setSubRegions] = useState<{ code: string; name: string }[]>([]);
  const [config, setConfig] = useState<TaxConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Generic edit state — stores the full config_data as a mutable object
  const [editing, setEditing] = useState(false);
  const [editTaxYear, setEditTaxYear] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedRegion = regions.find((r) => String(r.id) === regionId);
  const rc = selectedRegion?.code ?? "uk";

  useEffect(() => {
    void (async () => {
      try {
        const rs = await api.get<Region[]>("/api/regions");
        setRegions(rs);
        if (rs.length && !regionId) {
          const preferred = myRegionId ? rs.find((r) => r.id === myRegionId) : null;
          setRegionId(String(preferred?.id ?? rs[0].id));
        }
      } catch (err) {
        setError((err as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!regionId) return;
    setLoading(true);
    setError(null);
    try {
      const url = subRegion
        ? `/api/settings/tax?region_id=${regionId}&sub_region=${subRegion}`
        : `/api/settings/tax?region_id=${regionId}`;
      const res = await api.get<TaxConfig>(url);
      setConfig(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [regionId, subRegion]);

  useEffect(() => {
    if (!regionId) return;
    void (async () => {
      try {
        const res = await api.get<{ sub_regions: { code: string; name: string }[] }>(
          `/api/settings/tax/sub-regions?region_id=${regionId}`,
        );
        setSubRegions(res.sub_regions ?? []);
      } catch {
        setSubRegions([]);
      }
    })();
  }, [regionId]);

  useEffect(() => {
    setEditing(false);
    void load();
  }, [load]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deepClone = (o: any) => JSON.parse(JSON.stringify(o));

  const startEdit = () => {
    if (config?.configured && config.config_data) {
      setEditTaxYear(config.tax_year ?? "");
      setEditData(deepClone(config.config_data));
      setEditNotes(config.notes ?? "");
    } else {
      setEditTaxYear("");
      setEditData(defaultConfigForRegion(rc));
      setEditNotes("");
    }
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (config?.configured && config.id) {
        await api.put(`/api/settings/tax/${config.id}`, {
          config_data: editData,
          notes: editNotes || undefined,
        });
      } else {
        await api.post("/api/settings/tax", {
          region_id: Number(regionId),
          tax_year: editTaxYear,
          sub_region: subRegion || undefined,
          config_data: editData,
          notes: editNotes || undefined,
        });
      }
      setEditing(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cd: Record<string, any> | undefined = config?.config_data as any;
  const fc = (v: number) => fmtCurrency(v, rc);

  return (
    <div className="space-y-4">
      {/* Region selector */}
      <div className="flex flex-wrap gap-2">
        {regions.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => { setRegionId(String(r.id)); setSubRegion(""); }}
            className={[
              "rounded-lg px-4 py-2 text-sm font-medium",
              String(r.id) === regionId
                ? "bg-pme-blue text-white"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50",
            ].join(" ")}
          >
            {r.name}
          </button>
        ))}
      </div>

      {/* Sub-region selector */}
      {subRegions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {rc === "us" ? "State:" : rc === "es" ? "Region:" : "Sub-region:"}
          </span>
          <button
            type="button"
            onClick={() => setSubRegion("")}
            className={[
              "rounded-lg px-3 py-1.5 text-xs font-medium",
              !subRegion
                ? "bg-pme-blue text-white"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50",
            ].join(" ")}
          >
            {rc === "us" ? "Federal" : rc === "es" ? "Nacional" : "National"}
          </button>
          {subRegions.map((sr) => (
            <button
              key={sr.code}
              type="button"
              onClick={() => setSubRegion(sr.code)}
              className={[
                "rounded-lg px-3 py-1.5 text-xs font-medium",
                subRegion === sr.code
                  ? "bg-pme-blue text-white"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              {sr.code.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {!editing && config && !config.configured && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
          <p className="text-sm text-gray-500">
            No tax configuration for {selectedRegion?.name ?? "this region"}.
          </p>
          <button type="button" onClick={startEdit}
            className="mt-3 rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">
            Add Tax Year
          </button>
        </div>
      )}

      {/* ── View mode ────────────────────────────────────── */}
      {!editing && config?.configured && cd && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Tax Year: {config.tax_year}
            </h3>
            <button type="button" onClick={startEdit}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">
              Edit
            </button>
          </div>

          {/* Generic bands display — works for any region */}
          {renderBandsView(cd, rc, fc)}

          {/* Region-specific extra sections */}
          {rc === "uk" && cd.employee_ni && (
            <>
              <SectionCard title="Employee National Insurance">
                <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                  <Detail label="Primary Threshold" value={fc(cd.employee_ni.primary_threshold)} />
                  <Detail label="Upper Earnings Limit" value={fc(cd.employee_ni.upper_earnings_limit)} />
                  <Detail label="Main Rate (below UEL)" value={fmtPct(cd.employee_ni.main_rate)} />
                  <Detail label="Upper Rate (above UEL)" value={fmtPct(cd.employee_ni.upper_rate)} />
                </dl>
              </SectionCard>
              {cd.employer_ni && (
                <SectionCard title="Employer National Insurance">
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <Detail label="Secondary Threshold" value={fc(cd.employer_ni.secondary_threshold)} />
                    <Detail label="Rate" value={fmtPct(cd.employer_ni.rate)} />
                  </dl>
                </SectionCard>
              )}
            </>
          )}

          {rc === "za" && (
            <>
              {cd.income_tax?.rebates && (
                <SectionCard title="Tax Rebates">
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                    <Detail label="Primary" value={fc(cd.income_tax.rebates.primary)} />
                    <Detail label="Secondary" value={fc(cd.income_tax.rebates.secondary)} />
                    <Detail label="Tertiary" value={fc(cd.income_tax.rebates.tertiary)} />
                  </dl>
                </SectionCard>
              )}
              {cd.uif && (
                <SectionCard title="UIF (Unemployment Insurance)">
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                    <Detail label="Employee Rate" value={fmtPct(cd.uif.employee_rate)} />
                    <Detail label="Employer Rate" value={fmtPct(cd.uif.employer_rate)} />
                    <Detail label="Monthly Ceiling" value={fc(cd.uif.ceiling)} />
                  </dl>
                </SectionCard>
              )}
              {cd.sdl && (
                <SectionCard title="SDL (Skills Development Levy)">
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <Detail label="Rate" value={fmtPct(cd.sdl.rate)} />
                    <Detail label="Annual Threshold" value={fc(cd.sdl.threshold)} />
                  </dl>
                </SectionCard>
              )}
            </>
          )}

          {rc === "es" && cd.social_security && (
            <SectionCard title="Social Security">
              <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <Detail label="Employee Rate" value={fmtPct(cd.social_security.employee_rate)} />
                <Detail label="Employer Rate" value={fmtPct(cd.social_security.employer_rate)} />
              </dl>
            </SectionCard>
          )}

          {rc === "us" && (
            <>
              {cd.social_security && (
                <SectionCard title="Social Security">
                  <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <Detail label="Rate" value={fmtPct(cd.social_security.rate)} />
                    <Detail label="Wage Base" value={fc(cd.social_security.wage_base)} />
                  </dl>
                </SectionCard>
              )}
              {cd.medicare && (
                <SectionCard title="Medicare">
                  <dl className="text-sm"><Detail label="Rate" value={fmtPct(cd.medicare.rate)} /></dl>
                </SectionCard>
              )}
              {cd.state_tax_note && (
                <SectionCard title="State Tax">
                  <p className="text-sm text-gray-600">{cd.state_tax_note}</p>
                </SectionCard>
              )}
            </>
          )}

          {config.notes && (
            <SectionCard title="Notes">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{config.notes}</p>
            </SectionCard>
          )}
        </div>
      )}

      {/* ── Edit mode ────────────────────────────────────── */}
      {editing && (
        <div className="space-y-6">
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">Tax Year</span>
            <input type="text" value={editTaxYear}
              onChange={(e) => setEditTaxYear(e.target.value)}
              placeholder="e.g. 2026/27"
              disabled={!!(config?.configured && config.id)}
              className="mt-1 w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500" />
          </label>

          {/* Bands editor — works for any region's band arrays */}
          {renderBandsEditor(editData, rc, setEditData)}

          {/* Region-specific extra fields */}
          {rc === "uk" && renderUKEditFields(editData, setEditData)}
          {rc === "za" && renderZAEditFields(editData, setEditData)}
          {rc === "es" && renderESEditFields(editData, setEditData)}
          {rc === "us" && renderUSEditFields(editData, setEditData)}

          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">Notes</span>
            <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
              rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>

          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tax helpers ──────────────────────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {children}
    </section>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultConfigForRegion(code: string): Record<string, any> {
  switch (code) {
    case "za":
      return {
        income_tax: { bands: [{ name: "Band 1", from: 0, to: 237100, rate: 0.18 }], rebates: { primary: 17235, secondary: 9444, tertiary: 3145 } },
        uif: { employee_rate: 0.01, employer_rate: 0.01, ceiling: 17712 },
        sdl: { rate: 0.01, threshold: 500000 },
      };
    case "es":
      return {
        irpf: { bands: [{ name: "Tramo 1", from: 0, to: 12450, rate: 0.19 }] },
        social_security: { employee_rate: 0.0635, employer_rate: 0.299 },
      };
    case "us":
      return {
        federal_tax: { bands: [{ name: "10% bracket", from: 0, to: 11600, rate: 0.1 }] },
        social_security: { rate: 0.062, wage_base: 168600 },
        medicare: { rate: 0.0145 },
        state_tax_note: "Varies by state — configure per employee if needed",
      };
    default: // uk
      return {
        income_tax: { personal_allowance: 12570, personal_allowance_taper_threshold: 100000, bands: [{ name: "Basic rate", from: 12571, to: 50270, rate: 0.2 }] },
        employee_ni: { primary_threshold: 12570, upper_earnings_limit: 50270, main_rate: 0.08, upper_rate: 0.02 },
        employer_ni: { secondary_threshold: 5000, rate: 0.15 },
      };
  }
}

function getBandsPath(code: string): { key: string; label: string } {
  switch (code) {
    case "za": return { key: "income_tax.bands", label: "Income Tax Bands" };
    case "es": return { key: "irpf.bands", label: "IRPF Bands" };
    case "us": return { key: "federal_tax.bands", label: "Federal Tax Brackets" };
    default: return { key: "income_tax.bands", label: "Income Tax Bands" };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedBands(data: Record<string, any>, path: string): any[] {
  const parts = path.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let obj: any = data;
  for (const p of parts) {
    if (!obj || typeof obj !== "object") return [];
    obj = obj[p];
  }
  return Array.isArray(obj) ? obj : [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderBandsView(cd: Record<string, any>, rc: string, fc: (v: number) => string) {
  const { key, label } = getBandsPath(rc);
  const bands = getNestedBands(cd, key);
  if (!bands.length) return null;

  const showExtra = rc === "uk" && cd.income_tax;

  return (
    <SectionCard title={label}>
      {showExtra && (
        <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 mb-3">
          <Detail label="Personal Allowance" value={fc(cd.income_tax.personal_allowance ?? 0)} />
          <Detail label="Taper Threshold" value={fc(cd.income_tax.personal_allowance_taper_threshold ?? 0)} />
        </dl>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Band</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {bands.map((band: { name?: string; from: number; to: number | null; rate: number }, i: number) => (
              <tr key={i}>
                <td className="px-3 py-2 text-gray-700">{band.name ?? `Band ${i + 1}`}</td>
                <td className="px-3 py-2 font-mono tabular-nums">{fc(band.from)}</td>
                <td className="px-3 py-2 font-mono tabular-nums">{band.to === null ? "No limit" : fc(band.to)}</td>
                <td className="px-3 py-2 font-mono tabular-nums">{fmtPct(band.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderBandsEditor(data: Record<string, any>, rc: string, setData: (fn: (prev: Record<string, any>) => Record<string, any>) => void) {
  const { key, label } = getBandsPath(rc);
  const [parentKey, childKey] = key.split(".");
  const bands = getNestedBands(data, key);

  const updateBand = (idx: number, field: string, value: string) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const arr = next[parentKey]?.[childKey];
      if (!arr?.[idx]) return prev;
      if (field === "name") arr[idx].name = value;
      else if (field === "noLimit") arr[idx].to = value === "true" ? null : 0;
      else arr[idx][field] = Number(value) || 0;
      return next;
    });
  };

  const addBand = () => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[parentKey]) next[parentKey] = {};
      if (!next[parentKey][childKey]) next[parentKey][childKey] = [];
      next[parentKey][childKey].push({ name: "", from: 0, to: null, rate: 0 });
      return next;
    });
  };

  const removeBand = (idx: number) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[parentKey]?.[childKey]?.splice(idx, 1);
      return next;
    });
  };

  const showUKExtras = rc === "uk";

  return (
    <SectionCard title={label}>
      {showUKExtras && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 mb-3">
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">Personal Allowance</span>
            <input type="number" value={data.income_tax?.personal_allowance ?? 0}
              onChange={(e) => setData((p) => {
                const n = JSON.parse(JSON.stringify(p));
                if (!n.income_tax) n.income_tax = {};
                n.income_tax.personal_allowance = Number(e.target.value) || 0;
                return n;
              })}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700">Taper Threshold</span>
            <input type="number" value={data.income_tax?.personal_allowance_taper_threshold ?? 0}
              onChange={(e) => setData((p) => {
                const n = JSON.parse(JSON.stringify(p));
                if (!n.income_tax) n.income_tax = {};
                n.income_tax.personal_allowance_taper_threshold = Number(e.target.value) || 0;
                return n;
              })}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-700">Bands</span>
        <button type="button" onClick={addBand}
          className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50">
          + Add Band
        </button>
      </div>
      <div className="space-y-3">
        {bands.map((_: unknown, i: number) => {
          const band = bands[i];
          return (
            <div key={i} className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="flex items-center justify-between mb-2">
                <input type="text" value={band.name ?? ""} onChange={(e) => updateBand(i, "name", e.target.value)}
                  placeholder="Band name" className="rounded-lg border border-gray-300 px-2 py-1 text-sm font-medium" />
                <button type="button" onClick={() => removeBand(i)} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs">
                  <span className="block text-gray-500">From</span>
                  <input type="number" value={band.from ?? 0} onChange={(e) => updateBand(i, "from", e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm font-mono tabular-nums" />
                </label>
                <label className="text-xs">
                  <span className="block text-gray-500">To{band.to === null ? " (no limit)" : ""}</span>
                  {band.to !== null ? (
                    <input type="number" value={band.to} onChange={(e) => updateBand(i, "to", e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm font-mono tabular-nums" />
                  ) : (
                    <p className="mt-0.5 px-2 py-1 text-sm text-gray-400">No upper limit</p>
                  )}
                  <button type="button" onClick={() => updateBand(i, "noLimit", band.to === null ? "false" : "true")}
                    className="mt-1 text-[11px] text-pme-blue hover:underline">
                    {band.to === null ? "Set upper limit" : "Remove upper limit"}
                  </button>
                </label>
                <label className="text-xs">
                  <span className="block text-gray-500">Rate (decimal)</span>
                  <input type="number" step="0.01" value={band.rate ?? 0} onChange={(e) => updateBand(i, "rate", e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm font-mono tabular-nums" />
                  <span className="text-gray-400">{fmtPct(band.rate ?? 0)}</span>
                </label>
              </div>
            </div>
          );
        })}
        {bands.length === 0 && <p className="py-4 text-center text-sm text-gray-400">No bands yet. Click "+ Add Band".</p>}
      </div>
    </SectionCard>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium text-gray-700">{label}</span>
      <input type="number" step="any" value={value} onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
    </label>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderUKEditFields(data: Record<string, any>, setData: (fn: (p: Record<string, any>) => Record<string, any>) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const upd = (section: string, field: string, value: number) => setData((p: Record<string, any>) => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n[section]) n[section] = {};
    n[section][field] = value;
    return n;
  });
  const eni = data.employee_ni ?? {};
  const erni = data.employer_ni ?? {};
  return (
    <>
      <SectionCard title="Employee National Insurance">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumField label="Primary Threshold" value={eni.primary_threshold ?? 0} onChange={(v) => upd("employee_ni", "primary_threshold", v)} />
          <NumField label="Upper Earnings Limit" value={eni.upper_earnings_limit ?? 0} onChange={(v) => upd("employee_ni", "upper_earnings_limit", v)} />
          <NumField label="Main Rate (e.g. 0.08 = 8%)" value={eni.main_rate ?? 0} onChange={(v) => upd("employee_ni", "main_rate", v)} />
          <NumField label="Upper Rate (e.g. 0.02 = 2%)" value={eni.upper_rate ?? 0} onChange={(v) => upd("employee_ni", "upper_rate", v)} />
        </div>
      </SectionCard>
      <SectionCard title="Employer National Insurance">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumField label="Secondary Threshold" value={erni.secondary_threshold ?? 0} onChange={(v) => upd("employer_ni", "secondary_threshold", v)} />
          <NumField label="Rate (e.g. 0.15 = 15%)" value={erni.rate ?? 0} onChange={(v) => upd("employer_ni", "rate", v)} />
        </div>
      </SectionCard>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderZAEditFields(data: Record<string, any>, setData: (fn: (p: Record<string, any>) => Record<string, any>) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const upd = (section: string, field: string, value: number) => setData((p: Record<string, any>) => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n[section]) n[section] = {};
    n[section][field] = value;
    return n;
  });
  const rebates = data.income_tax?.rebates ?? {};
  const uif = data.uif ?? {};
  const sdl = data.sdl ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updRebate = (field: string, value: number) => setData((p: Record<string, any>) => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n.income_tax) n.income_tax = {};
    if (!n.income_tax.rebates) n.income_tax.rebates = {};
    n.income_tax.rebates[field] = value;
    return n;
  });
  return (
    <>
      <SectionCard title="Tax Rebates">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <NumField label="Primary Rebate" value={rebates.primary ?? 0} onChange={(v) => updRebate("primary", v)} />
          <NumField label="Secondary Rebate" value={rebates.secondary ?? 0} onChange={(v) => updRebate("secondary", v)} />
          <NumField label="Tertiary Rebate" value={rebates.tertiary ?? 0} onChange={(v) => updRebate("tertiary", v)} />
        </div>
      </SectionCard>
      <SectionCard title="UIF (Unemployment Insurance)">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <NumField label="Employee Rate" value={uif.employee_rate ?? 0} onChange={(v) => upd("uif", "employee_rate", v)} />
          <NumField label="Employer Rate" value={uif.employer_rate ?? 0} onChange={(v) => upd("uif", "employer_rate", v)} />
          <NumField label="Monthly Ceiling" value={uif.ceiling ?? 0} onChange={(v) => upd("uif", "ceiling", v)} />
        </div>
      </SectionCard>
      <SectionCard title="SDL (Skills Development Levy)">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumField label="Rate" value={sdl.rate ?? 0} onChange={(v) => upd("sdl", "rate", v)} />
          <NumField label="Annual Threshold" value={sdl.threshold ?? 0} onChange={(v) => upd("sdl", "threshold", v)} />
        </div>
      </SectionCard>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderESEditFields(data: Record<string, any>, setData: (fn: (p: Record<string, any>) => Record<string, any>) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const upd = (section: string, field: string, value: number) => setData((p: Record<string, any>) => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n[section]) n[section] = {};
    n[section][field] = value;
    return n;
  });
  const ss = data.social_security ?? {};
  return (
    <SectionCard title="Social Security">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <NumField label="Employee Rate" value={ss.employee_rate ?? 0} onChange={(v) => upd("social_security", "employee_rate", v)} />
        <NumField label="Employer Rate" value={ss.employer_rate ?? 0} onChange={(v) => upd("social_security", "employer_rate", v)} />
      </div>
    </SectionCard>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderUSEditFields(data: Record<string, any>, setData: (fn: (p: Record<string, any>) => Record<string, any>) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const upd = (section: string, field: string, value: number | string) => setData((p: Record<string, any>) => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n[section]) n[section] = {};
    n[section][field] = value;
    return n;
  });
  const ss = data.social_security ?? {};
  const mc = data.medicare ?? {};
  return (
    <>
      <SectionCard title="Social Security">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumField label="Rate" value={ss.rate ?? 0} onChange={(v) => upd("social_security", "rate", v)} />
          <NumField label="Wage Base" value={ss.wage_base ?? 0} onChange={(v) => upd("social_security", "wage_base", v)} />
        </div>
      </SectionCard>
      <SectionCard title="Medicare">
        <NumField label="Rate" value={mc.rate ?? 0} onChange={(v) => upd("medicare", "rate", v)} />
      </SectionCard>
      <SectionCard title="State Tax">
        <label className="block text-sm">
          <span className="block text-xs font-medium text-gray-700">Note</span>
          <input type="text" value={data.state_tax_note ?? ""}
            onChange={(e) => setData((p) => ({ ...JSON.parse(JSON.stringify(p)), state_tax_note: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        </label>
      </SectionCard>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 4: Access Levels (super_admin only)
 * ──────────────────────────────────────────────────────────────────────────── */

interface LevelHolder {
  id: number;
  name: string;
  job_title: string | null;
  site_name: string | null;
}

/** Every access-level write is Super Admin only on the server — say so
 *  plainly instead of surfacing a bare 403. */
function accessLevelError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "Only a Super Admin can change access levels.";
    return err.message;
  }
  return "Something went wrong — nothing was changed.";
}

function PermissionsTab() {
  const { isSuperAdmin } = useAuth();
  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [applyHolders, setApplyHolders] = useState<LevelHolder[] | null>(null);
  const [applying, setApplying] = useState(false);

  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formIsAdmin, setFormIsAdmin] = useState(false);
  const [formRank, setFormRank] = useState(10);
  const [formRegionScope, setFormRegionScope] = useState<number[]>([]);
  const [formAllRegions, setFormAllRegions] = useState(true);
  const [formSiteVisibility, setFormSiteVisibility] = useState(false);
  const [formDeptScope, setFormDeptScope] = useState<number[]>([]);
  const [formAllDepts, setFormAllDepts] = useState(true);
  const [formPerms, setFormPerms] = useState<Record<PermKey, boolean>>({} as Record<PermKey, boolean>);
  const [formDocPerms, setFormDocPerms] = useState<DocumentPermission[]>([]);
  const [formVisibleTabs, setFormVisibleTabs] = useState<string[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [allDepartments, setAllDepartments] = useState<{ id: number; name: string; site_id: number }[]>([]);
  const [allSites, setAllSites] = useState<{ id: number; name: string; region_id: number }[]>([]);
  const [docCategories, setDocCategories] = useState<{ id: number; name: string }[]>([]);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lvls, regs, sites, cats] = await Promise.all([
        api.get<AccessLevel[]>("/api/access-levels"),
        api.get<Region[]>("/api/regions"),
        api.get<{ id: number; name: string; region_id: number }[]>("/api/sites"),
        api.get<{ id: number; name: string }[]>("/api/documents/categories"),
      ]);
      setLevels(lvls);
      setRegions(regs);
      setAllSites(sites);
      setDocCategories(cats);
      const deptPromises = sites.map((s) =>
        api.get<{ id: number; name: string; site_id: number }[]>(`/api/departments?site_id=${s.id}`)
      );
      const deptsByGroup = await Promise.all(deptPromises);
      setAllDepartments(deptsByGroup.flat());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = levels.find((l) => l.id === selectedId) ?? null;
  const isSystemRaw = selected?.is_system ?? false;
  const isSystem = isSystemRaw && !isSuperAdmin;
  const isSuperAdminLevel = selected?.name === "Super Admin";

  const resetForm = () => {
    setFormName("");
    setFormDesc("");
    setFormIsAdmin(false);
    setFormRank(10);
    setFormRegionScope([]);
    setFormAllRegions(true);
    setFormDeptScope([]);
    setFormAllDepts(true);
    const perms = {} as Record<PermKey, boolean>;
    for (const key of PERMISSION_KEYS) perms[key] = false;
    setFormPerms(perms);
    setFormDocPerms([]);
    setFormVisibleTabs([]);
  };

  const selectLevel = (al: AccessLevel) => {
    setCreating(false);
    setSelectedId(al.id);
    setApplyHolders(null);
    setFormName(al.name);
    setFormDesc(al.description ?? "");
    setFormIsAdmin(al.is_admin);
    setFormRank(al.rank ?? 10);
    setFormSiteVisibility(!!al.site_visibility_enabled);
    const rs = al.region_scope ?? [];
    setFormRegionScope(rs);
    setFormAllRegions(rs.length === 0);
    const ds = al.department_scope ?? [];
    setFormDeptScope(ds);
    setFormAllDepts(ds.length === 0);
    const perms = {} as Record<PermKey, boolean>;
    for (const key of PERMISSION_KEYS) {
      perms[key] = (al as unknown as Record<string, boolean>)[key] ?? false;
    }
    setFormPerms(perms);
    const existingDocPerms = al.document_permissions ?? [];
    if (existingDocPerms.length > 0) {
      setFormDocPerms(existingDocPerms);
    } else if (perms.can_view_documents && docCategories.length > 0) {
      setFormDocPerms(docCategories.map((c) => ({
        document_category_id: c.id,
        category_name: c.name,
        can_view: false,
        can_upload: false,
        can_delete: false,
      })));
    } else {
      setFormDocPerms([]);
    }
    setFormVisibleTabs((al as unknown as Record<string, string[] | null>).visible_tabs ?? []);
    setError(null);
    setNotice(null);
  };

  const startCreate = () => {
    resetForm();
    setSelectedId(null);
    setCreating(true);
    setError(null);
    setNotice(null);
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body = {
        name: formName,
        description: formDesc || null,
        is_admin: formIsAdmin,
        rank: formRank,
        site_visibility_enabled: formSiteVisibility,
        region_scope: formAllRegions ? null : formRegionScope,
        department_scope: formAllDepts ? null : formDeptScope,
        ...formPerms,
        document_permissions: formDocPerms.map((dp) => ({
          document_category_id: dp.document_category_id,
          can_view: dp.can_view,
          can_upload: dp.can_upload,
          can_delete: dp.can_delete,
        })),
        visible_tabs: formVisibleTabs.length > 0 ? formVisibleTabs : null,
      };
      if (selectedId && !creating) {
        await api.put(`/api/access-levels/${selectedId}`, body);
        setNotice(
          selected && selected.employee_count > 0
            ? "Access level saved. Use Apply to push the change onto the people who hold it."
            : "Access level saved.",
        );
      } else {
        const created = await api.post<AccessLevel>("/api/access-levels", body);
        setNotice("Access level created.");
        setCreating(false);
        setSelectedId(created.id);
      }
      await load();
    } catch (err) {
      setError(accessLevelError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteLevel = async () => {
    if (!selectedId || !confirm("Delete this access level?")) return;
    setError(null);
    try {
      await api.delete(`/api/access-levels/${selectedId}`);
      setNotice("Access level deleted.");
      setSelectedId(null);
      setCreating(false);
      await load();
    } catch (err) {
      setError(accessLevelError(err));
    }
  };

  // Step 1 of "Apply to N employees": show who holds the level first.
  const showApplyHolders = async () => {
    if (!selectedId) return;
    setError(null);
    setNotice(null);
    try {
      const res = await api.get<{ employees: LevelHolder[] }>(`/api/access-levels/${selectedId}/employees`);
      setApplyHolders(res.employees);
    } catch (err) {
      setError(accessLevelError(err));
    }
  };

  // Step 2: push the saved level's permissions onto those employees.
  const applyToEmployees = async () => {
    if (!selectedId) return;
    setError(null);
    setApplying(true);
    try {
      const res = await api.post<{ updated: number; employees?: string[] }>(`/api/access-levels/${selectedId}/apply`);
      const who = res.employees?.length ? `: ${res.employees.join(", ")}` : "";
      setNotice(`Permissions applied to ${res.updated} employee${res.updated === 1 ? "" : "s"}${who}.`);
      setApplyHolders(null);
    } catch (err) {
      setError(accessLevelError(err));
    } finally {
      setApplying(false);
    }
  };

  const duplicateLevel = async (id: number) => {
    setError(null);
    setNotice(null);
    try {
      const created = await api.post<AccessLevel>(`/api/access-levels/${id}/duplicate`);
      setNotice("Access level duplicated.");
      await load();
      selectLevel(created);
      setTimeout(() => nameInputRef.current?.select(), 50);
    } catch (err) {
      setError(accessLevelError(err));
    }
  };

  const togglePerm = (key: PermKey) => {
    const newValue = !formPerms[key];
    setFormPerms((prev) => ({ ...prev, [key]: newValue }));
    if (key === "can_view_documents" && newValue && formDocPerms.length === 0 && docCategories.length > 0) {
      setFormDocPerms(docCategories.map((c) => ({
        document_category_id: c.id,
        category_name: c.name,
        can_view: false,
        can_upload: false,
        can_delete: false,
      })));
    }
  };

  const toggleRegion = (regionId: number) => {
    setFormRegionScope((prev) =>
      prev.includes(regionId)
        ? prev.filter((id) => id !== regionId)
        : [...prev, regionId]
    );
  };

  const toggleDept = (deptId: number) => {
    setFormDeptScope((prev) =>
      prev.includes(deptId)
        ? prev.filter((id) => id !== deptId)
        : [...prev, deptId]
    );
  };

  const filteredDepartments = formAllRegions
    ? allDepartments
    : allDepartments.filter((d) => {
        const site = allSites.find((s) => s.id === d.site_id);
        return site && formRegionScope.includes(site.region_id);
      });

  const toggleDocPerm = (catId: number, field: "can_view" | "can_upload" | "can_delete") => {
    setFormDocPerms((prev) =>
      prev.map((dp) =>
        dp.document_category_id === catId ? { ...dp, [field]: !dp[field] } : dp
      )
    );
  };

  const toggleAllDocPerm = (field: "can_view" | "can_upload" | "can_delete") => {
    const allOn = formDocPerms.every((dp) => dp[field]);
    setFormDocPerms((prev) =>
      prev.map((dp) => ({ ...dp, [field]: !allOn }))
    );
  };

  const permCount = (al: AccessLevel): number => {
    return PERMISSION_KEYS.filter(
      (k) => (al as unknown as Record<string, boolean>)[k],
    ).length;
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  const showForm = creating || selectedId !== null;

  return (
    <div className="space-y-4">
      {/* With a level open, messages show beside its buttons instead. */}
      {!showForm && error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {!showForm && notice && (
        <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>
      )}

      <div className="flex gap-6">
        <div className="w-[40%] shrink-0 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Access Levels</p>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={startCreate}
                className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
              >
                Add Access Level
              </button>
            )}
          </div>
          {!isSuperAdmin && (
            <p className="rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
              View only. Only a Super Admin can change access levels.
            </p>
          )}

          <div className="max-h-[calc(100vh-280px)] space-y-2 overflow-y-auto pr-1">
            {levels.map((al) => {
              const active = !creating && selectedId === al.id;
              return (
                <button
                  key={al.id}
                  type="button"
                  onClick={() => selectLevel(al)}
                  className={[
                    "w-full rounded-lg border p-3 text-left transition-colors",
                    active
                      ? "border-l-4 border-l-pme-blue border-t-gray-200 border-r-gray-200 border-b-gray-200 bg-blue-50/50"
                      : "border-gray-200 bg-white hover:bg-gray-50",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">{al.name}</h4>
                    <div className="flex gap-1">
                      {al.is_system && (
                        <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                          System
                        </span>
                      )}
                      {al.is_admin && (
                        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                  {al.description && (
                    <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">{al.description}</p>
                  )}
                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-gray-400">
                    <span>Rank {al.rank}</span>
                    <span>·</span>
                    <span>{permCount(al)} permissions</span>
                    <span>·</span>
                    <span>{al.employee_count} employee{al.employee_count !== 1 ? "s" : ""}</span>
                    {isSuperAdmin && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); void duplicateLevel(al.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); void duplicateLevel(al.id); } }}
                        className="ml-auto text-pme-blue hover:underline"
                      >
                        Duplicate
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {!showForm && (
            <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
              <p className="text-sm text-gray-400">
                Select an access level to edit, or create a new one.
              </p>
            </div>
          )}

          {showForm && (
            <form
              onSubmit={submitForm}
              className="sticky top-4 space-y-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <h4 className="text-sm font-semibold text-gray-900">
                {creating ? "New Access Level" : `Edit: ${selected?.name ?? ""}`}
              </h4>

              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="block text-xs font-medium text-gray-700">Name</span>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                    disabled={isSystemRaw}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-xs font-medium text-gray-700">Description</span>
                  <textarea
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    disabled={isSystem}
                    rows={2}
                    className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formIsAdmin}
                  onChange={(e) => setFormIsAdmin(e.target.checked)}
                  disabled={isSystem || isSuperAdminLevel}
                  className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                />
                <span className="text-gray-700">Admin panel access</span>
              </label>

              <label className="block text-sm">
                <span className="block text-xs font-medium text-gray-700">Hierarchy Rank</span>
                <p className="text-[11px] text-gray-400">
                  Higher rank can manage lower rank. 1-99 for custom levels. Super Admin is locked at 100.
                </p>
                <input
                  type="number"
                  min={1}
                  max={isSuperAdminLevel ? 100 : 99}
                  value={formRank}
                  onChange={(e) => setFormRank(Number(e.target.value))}
                  disabled={isSystem || isSuperAdminLevel}
                  className="mt-1 w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
              </label>

              <div className="space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Region access
                </h5>
                <p className="text-[11px] text-gray-400">
                  Which regions can this access level manage? Leave empty for all regions.
                </p>

                <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={formSiteVisibility}
                    onChange={(e) => setFormSiteVisibility(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                  />
                  <span>
                    <span className="text-gray-700 font-medium">Site Visibility</span>
                    <span className="mt-0.5 block text-[11px] text-gray-500">
                      When on, holders can be granted admin-data visibility of specific sites
                      on their employee record. When off they are hard-locked to their own
                      site and the site selector does not appear for them.
                    </span>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formAllRegions}
                    onChange={(e) => {
                      setFormAllRegions(e.target.checked);
                      if (e.target.checked) setFormRegionScope([]);
                    }}
                    disabled={isSystem}
                    className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                  />
                  <span className="text-gray-700">All regions</span>
                </label>
                {!formAllRegions && (
                  <div className="flex flex-wrap gap-2 pl-6">
                    {regions.map((r) => (
                      <label key={r.id} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={formRegionScope.includes(r.id)}
                          onChange={() => toggleRegion(r.id)}
                          disabled={isSystem}
                          className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                        />
                        <span className="text-gray-700">{r.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Department access
                </h5>
                <p className="text-[11px] text-gray-400">
                  Which departments can this access level manage? Leave empty for all departments.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formAllDepts}
                    onChange={(e) => {
                      setFormAllDepts(e.target.checked);
                      if (e.target.checked) setFormDeptScope([]);
                    }}
                    disabled={isSystem}
                    className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                  />
                  <span className="text-gray-700">All departments</span>
                </label>
                {!formAllDepts && (
                  <div className="space-y-2 pl-6">
                    {allSites
                      .filter((s) => formAllRegions || formRegionScope.includes(s.region_id))
                      .map((site) => {
                        const siteDepts = filteredDepartments.filter((d) => d.site_id === site.id);
                        if (siteDepts.length === 0) return null;
                        return (
                          <div key={site.id}>
                            <p className="text-xs font-medium text-gray-600 mb-1">{site.name}</p>
                            <div className="flex flex-wrap gap-2 pl-2">
                              {siteDepts.map((d) => (
                                <label key={d.id} className="flex items-center gap-1.5 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={formDeptScope.includes(d.id)}
                                    onChange={() => toggleDept(d.id)}
                                    disabled={isSystem}
                                    className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                                  />
                                  <span className="text-gray-700">{d.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Permissions
                </h5>
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.label} className="space-y-1.5">
                    <h6 className="text-xs font-medium text-gray-600">{group.label}</h6>
                    <div className="grid grid-cols-1 gap-1.5 pl-2 md:grid-cols-2">
                      {group.keys.map((key) => (
                        <div key={key}>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={formPerms[key] ?? false}
                              onChange={() => togglePerm(key)}
                              disabled={isSystem || isSuperAdminLevel}
                              className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue disabled:opacity-50"
                            />
                            <span className={isSystem || isSuperAdminLevel ? "text-gray-400" : "text-gray-700"}>
                              {PERMISSION_LABELS[key]}
                            </span>
                          </label>
                          {key === "can_view_lateral_compensation" && (
                            <p className="ml-6 text-[10px] text-gray-400">Can see compensation for employees at the same access level rank</p>
                          )}
                          {key === "can_view_all_compensation" && (
                            <p className="ml-6 text-[10px] text-amber-600">This overrides all rank-based restrictions</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {formPerms.can_view_documents && (
                  <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                    <h6 className="text-xs font-medium text-gray-600">Document category permissions</h6>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                          <th className="pb-1.5 pr-3">Category</th>
                          <th className="pb-1.5 px-2 text-center">
                            <button type="button" onClick={() => toggleAllDocPerm("can_view")} className="hover:text-pme-blue">View</button>
                          </th>
                          <th className="pb-1.5 px-2 text-center">
                            <button type="button" onClick={() => toggleAllDocPerm("can_upload")} className="hover:text-pme-blue">Upload</button>
                          </th>
                          <th className="pb-1.5 px-2 text-center">
                            <button type="button" onClick={() => toggleAllDocPerm("can_delete")} className="hover:text-pme-blue">Delete</button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formDocPerms.map((dp) => (
                          <tr key={dp.document_category_id} className="border-b border-gray-100 last:border-0">
                            <td className="py-1.5 pr-3 text-gray-700">{dp.category_name}</td>
                            <td className="py-1.5 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={dp.can_view}
                                onChange={() => toggleDocPerm(dp.document_category_id, "can_view")}
                                disabled={isSystem}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                              />
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={dp.can_upload}
                                onChange={() => toggleDocPerm(dp.document_category_id, "can_upload")}
                                disabled={isSystem}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                              />
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={dp.can_delete}
                                onChange={() => toggleDocPerm(dp.document_category_id, "can_delete")}
                                disabled={isSystem}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase text-gray-500">Visible Tabs</h4>
                <p className="text-xs text-gray-400 mb-2">Select which tabs employees with this level can see. Leave empty to show all.</p>
                <div className="flex flex-wrap gap-2">
                  {["dashboard", "employees", "reports", "leave", "training", "documents", "performance", "expenses", "announcements", "settings"].map((tabName) => (
                    <label key={tabName} className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={formVisibleTabs.includes(tabName)}
                        onChange={(e) => {
                          if (e.target.checked) setFormVisibleTabs([...formVisibleTabs, tabName]);
                          else setFormVisibleTabs(formVisibleTabs.filter((t) => t !== tabName));
                        }}
                        disabled={isSystem}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-pme-blue"
                      />
                      {tabName}
                    </label>
                  ))}
                </div>
              </div>

              {isSuperAdminLevel && (
                <p className="text-xs text-amber-600">Super Admin always has full access. Permissions cannot be changed.</p>
              )}

              {applyHolders && (
                <div className="space-y-2 rounded-lg border border-pme-blue/30 bg-blue-50/50 p-3 text-sm">
                  <p className="font-medium text-gray-900">
                    Apply the saved permissions of “{selected?.name}” to{" "}
                    {applyHolders.length === 1 ? "this person" : `these ${applyHolders.length} people`}:
                  </p>
                  <ul className="space-y-0.5 text-xs text-gray-700">
                    {applyHolders.map((h) => (
                      <li key={h.id}>
                        <span className="font-semibold">{h.name}</span>
                        {h.job_title && <span className="text-gray-500"> · {h.job_title}</span>}
                        {h.site_name && <span className="text-gray-400"> · {h.site_name}</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-500">
                    This uses the last SAVED version of the level — save first if you've just changed it.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void applyToEmployees()}
                      disabled={applying}
                      className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {applying ? "Applying..." : "Apply now"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setApplyHolders(null)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {(error || notice) && (
                <p
                  role="status"
                  className={`rounded-lg p-3 text-sm ${error ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}
                >
                  {error ?? notice}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                {isSuperAdmin && (
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {saving ? "Saving..." : creating ? "Create" : "Save"}
                  </button>
                )}
                {isSuperAdmin && !creating && selected && selected.employee_count > 0 && !applyHolders && (
                  <button
                    type="button"
                    onClick={() => void showApplyHolders()}
                    className="rounded-lg border border-pme-blue px-4 py-2 text-sm font-semibold text-pme-blue"
                  >
                    Apply to {selected.employee_count} employee{selected.employee_count !== 1 ? "s" : ""}…
                  </button>
                )}
                {isSuperAdmin && !creating && !isSystem && (
                  <button
                    type="button"
                    onClick={deleteLevel}
                    className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-600"
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(null);
                    setCreating(false);
                    resetForm();
                  }}
                  className="ml-auto rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  Close
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 5: Leave Types
 * ──────────────────────────────────────────────────────────────────────────── */

function LeaveTypesTab() {
  const { regionId: myRegionId } = useMyRegion();
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionId, setRegionId] = useState<string>("");
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formIsPaid, setFormIsPaid] = useState(true);
  const [formRequiresApproval, setFormRequiresApproval] = useState(true);
  const [formRequiresDocumentation, setFormRequiresDocumentation] = useState(false);
  const [formDocThresholdDays, setFormDocThresholdDays] = useState<string>("");
  const [formMaxConsecutive, setFormMaxConsecutive] = useState<string>("");
  const [formDefaultDays, setFormDefaultDays] = useState<string>("");
  const [formDepartmentIds, setFormDepartmentIds] = useState<number[]>([]);
  const [formColourHex, setFormColourHex] = useState("#3B82F6");
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [rs, ds] = await Promise.all([
          api.get<Region[]>("/api/regions"),
          api.get<Department[]>("/api/departments").catch(() => [] as Department[]),
        ]);
        setRegions(rs);
        setDepartments(ds);
        if (rs.length && !regionId) {
          const preferred = myRegionId ? rs.find((r) => r.id === myRegionId) : null;
          setRegionId(String(preferred?.id ?? rs[0].id));
        }
      } catch (err) {
        setError((err as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!regionId) return;
    setError(null);
    try {
      const params = new URLSearchParams({ region_id: regionId });
      setLeaveTypes(
        await api.get<LeaveType[]>(`/api/leave/types?${params.toString()}`),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }, [regionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setFormName("");
    setFormCode("");
    setFormIsPaid(true);
    setFormRequiresApproval(true);
    setFormRequiresDocumentation(false);
    setFormDocThresholdDays("");
    setFormMaxConsecutive("");
    setFormDefaultDays("");
    setFormDepartmentIds([]);
    setFormColourHex("#3B82F6");
    setFormIsActive(true);
    setEditingId(null);
    setShowForm(false);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (lt: LeaveType) => {
    setEditingId(lt.id);
    setFormName(lt.name);
    setFormCode(lt.code);
    setFormIsPaid(lt.is_paid);
    setFormRequiresApproval(lt.requires_approval);
    setFormRequiresDocumentation(lt.requires_documentation);
    setFormDocThresholdDays(lt.documentation_threshold_days != null ? String(lt.documentation_threshold_days) : "");
    setFormMaxConsecutive(lt.max_consecutive_days != null ? String(lt.max_consecutive_days) : "");
    setFormDefaultDays(lt.default_days != null ? String(Number(lt.default_days)) : "");
    setFormDepartmentIds(lt.department_ids ?? []);
    setFormColourHex(lt.colour_hex);
    setFormIsActive(lt.is_active);
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body = {
        region_id: Number(regionId),
        name: formName,
        code: formCode,
        is_paid: formIsPaid,
        requires_approval: formRequiresApproval,
        requires_documentation: formRequiresDocumentation,
        documentation_threshold_days: formDocThresholdDays ? Number(formDocThresholdDays) : null,
        max_consecutive_days: formMaxConsecutive ? Number(formMaxConsecutive) : null,
        default_days: formDefaultDays ? Number(formDefaultDays) : null,
        department_ids: formDepartmentIds.length > 0 ? formDepartmentIds : null,
        colour_hex: formColourHex,
        is_active: formIsActive,
      };
      if (editingId) {
        await api.put(`/api/leave/types/${editingId}`, body);
        setNotice("Leave type updated.");
      } else {
        await api.post("/api/leave/types", body);
        setNotice("Leave type created.");
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteLeaveType = async (id: number) => {
    if (!confirm("Delete this leave type?")) return;
    setError(null);
    try {
      await api.delete(`/api/leave/types/${id}`);
      setNotice("Leave type deleted.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-700">Region</span>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={openAdd}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          Add Leave Type
        </button>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {notice && (
        <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Days / Year</th>
              <th className="px-3 py-2">Applies To</th>
              <th className="px-3 py-2">Paid</th>
              <th className="px-3 py-2">Requires Approval</th>
              <th className="px-3 py-2">Requires Documentation</th>
              <th className="px-3 py-2">Colour</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {leaveTypes.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                  No leave types for this region.
                </td>
              </tr>
            )}
            {leaveTypes.map((lt) => (
              <tr key={lt.id}>
                <td className="px-3 py-2 font-medium text-gray-900">{lt.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{lt.code}</td>
                <td className="px-3 py-2 tabular-nums">
                  {lt.default_days != null ? Number(lt.default_days) : <span className="text-gray-400">Default</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {lt.department_ids && lt.department_ids.length > 0
                    ? lt.department_ids
                        .map((id) => departments.find((d) => d.id === id)?.name ?? `#${id}`)
                        .join(", ")
                    : "All departments"}
                </td>
                <td className="px-3 py-2">{lt.is_paid ? "Yes" : "No"}</td>
                <td className="px-3 py-2">{lt.requires_approval ? "Yes" : "No"}</td>
                <td className="px-3 py-2">{lt.requires_documentation ? "Yes" : "No"}</td>
                <td className="px-3 py-2">
                  <span
                    className="inline-block h-5 w-5 rounded border border-gray-200"
                    style={{ backgroundColor: lt.colour_hex }}
                    title={lt.colour_hex}
                  />
                </td>
                <td className="px-3 py-2">
                  <span
                    className={[
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                      lt.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600",
                    ].join(" ")}
                  >
                    {lt.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(lt)}
                    className="mr-2 text-xs text-pme-blue hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteLeaveType(lt.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <form
          onSubmit={submitForm}
          className="space-y-4 rounded-xl border border-gray-200 bg-white p-5"
        >
          <h4 className="text-sm font-semibold text-gray-900">
            {editingId ? "Edit Leave Type" : "New Leave Type"}
          </h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Name</span>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Code</span>
              <input
                type="text"
                value={formCode}
                onChange={(e) => setFormCode(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formIsPaid}
                onChange={(e) => setFormIsPaid(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
              />
              <span className="text-gray-700">Paid</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formRequiresApproval}
                onChange={(e) => setFormRequiresApproval(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
              />
              <span className="text-gray-700">Requires Approval</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formRequiresDocumentation}
                onChange={(e) => setFormRequiresDocumentation(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
              />
              <span className="text-gray-700">Requires Documentation</span>
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">
                Documentation Threshold (days)
              </span>
              <input
                type="number"
                value={formDocThresholdDays}
                onChange={(e) => setFormDocThresholdDays(e.target.value)}
                placeholder="Optional"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">
                Max Consecutive Days
              </span>
              <input
                type="number"
                value={formMaxConsecutive}
                onChange={(e) => setFormMaxConsecutive(e.target.value)}
                placeholder="Optional"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">
                Days Per Year
              </span>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formDefaultDays}
                onChange={(e) => setFormDefaultDays(e.target.value)}
                placeholder="e.g. 15"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-[11px] text-gray-400">
                Annual allowance in working days, excluding weekends and bank holidays.
                Leave blank to keep the built-in regional default.
              </span>
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Colour</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  value={formColourHex}
                  onChange={(e) => setFormColourHex(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={formColourHex}
                  onChange={(e) => setFormColourHex(e.target.value)}
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                />
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
              />
              <span className="text-gray-700">Active</span>
            </label>
          </div>

          {/* Department scoping — how one area (e.g. Shared Services) gets its own rules */}
          <div>
            <span className="block text-xs font-medium text-gray-700">Applies To</span>
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Leave unticked to apply this leave type to everyone in the region. Tick departments to
              limit it — for example a 15-day annual leave type that only Shared Services sees.
            </span>
            <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
              {departments.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={formDepartmentIds.includes(d.id)}
                    onChange={(e) =>
                      setFormDepartmentIds((prev) =>
                        e.target.checked ? [...prev, d.id] : prev.filter((id) => id !== d.id),
                      )
                    }
                    className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                  />
                  {d.name}
                  {d.site_name && <span className="text-gray-400">({d.site_name})</span>}
                </label>
              ))}
              {departments.length === 0 && (
                <p className="text-xs text-gray-400">No departments set up yet.</p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : editingId ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 6: Self-Service (super_admin only)
 * ──────────────────────────────────────────────────────────────────────────── */

function SelfServiceTab() {
  const [fields, setFields] = useState<SelfServiceField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Change request approval state
  const [pendingRequests, setPendingRequests] = useState<PendingChangeRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processingId, setProcessingId] = useState<number | null>(null);

  const loadPendingRequests = useCallback(async () => {
    try {
      const data = await api.get<PendingChangeRequest[]>("/api/self-service/change-requests/pending");
      setPendingRequests(data);
    } catch {
      // silently fail — the field config is the primary content
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<SelfServiceField[]>("/api/settings/self-service-fields");
        setFields(data);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    void loadPendingRequests();
  }, [loadPendingRequests]);

  const handleApprove = async (requestId: number) => {
    setProcessingId(requestId);
    try {
      await api.put(`/api/self-service/change-requests/${requestId}/approve`, {});
      setNotice("Change request approved.");
      await loadPendingRequests();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to approve request");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: number) => {
    if (!rejectReason.trim()) return;
    setProcessingId(requestId);
    try {
      await api.put(`/api/self-service/change-requests/${requestId}/reject`, { reason: rejectReason.trim() });
      setNotice("Change request rejected.");
      setRejectingId(null);
      setRejectReason("");
      await loadPendingRequests();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to reject request");
    } finally {
      setProcessingId(null);
    }
  };

  const updateAccessLevel = (fieldName: string, level: SelfServiceAccessLevel) => {
    setFields((prev) =>
      prev.map((f) =>
        f.field_name === fieldName ? { ...f, access_level: level } : f,
      ),
    );
  };

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put("/api/settings/self-service-fields", {
        fields: fields.map((f) => ({
          field_name: f.field_name,
          access_level: f.access_level,
        })),
      });
      setNotice("Self-service field configuration saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  // Group fields by field_group
  const grouped: Record<string, SelfServiceField[]> = {};
  for (const f of fields) {
    if (!grouped[f.field_group]) grouped[f.field_group] = [];
    grouped[f.field_group].push(f);
  }

  const sortedGroups = FIELD_GROUPS_ORDER.filter((g) => grouped[g]?.length);
  // Add any groups not in the predefined order
  for (const g of Object.keys(grouped)) {
    if (!sortedGroups.includes(g)) sortedGroups.push(g);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Configure which fields employees can view or edit through self-service.
        Changes require approval are routed to HR for review.
      </p>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {notice && (
        <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>
      )}

      {/* Pending Change Requests */}
      {!loadingRequests && pendingRequests.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Pending Change Requests
            </h4>
            <span className="inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
              {pendingRequests.length}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingRequests.map((req) => (
              <div key={req.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {req.employee_name}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-600">
                      <span className="font-medium">{req.field_label}:</span>{" "}
                      <span className="text-gray-400">{req.current_value || "(empty)"}</span>
                      {" → "}
                      <span className="font-medium text-gray-900">{req.requested_value || "(empty)"}</span>
                    </p>
                    {req.reason && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Reason: {req.reason}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-gray-400">
                      {new Date(req.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {rejectingId !== req.id && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(req.id)}
                          disabled={processingId === req.id}
                          className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                        >
                          {processingId === req.id ? "..." : "Approve"}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRejectingId(req.id); setRejectReason(""); }}
                          disabled={processingId === req.id}
                          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {rejectingId === req.id && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection..."
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                    />
                    <button
                      type="button"
                      onClick={() => handleReject(req.id)}
                      disabled={processingId === req.id || !rejectReason.trim()}
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      {processingId === req.id ? "..." : "Confirm"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRejectingId(null); setRejectReason(""); }}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedGroups.map((group) => (
        <div key={group} className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {group}
            </h4>
          </div>
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-4 py-2">Field Name</th>
                <th className="px-4 py-2">Access Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grouped[group].map((f) => (
                <tr key={f.field_name}>
                  <td className="px-4 py-2 font-medium text-gray-700">
                    {f.field_label}
                  </td>
                  <td className="px-4 py-2">
                    <select
                      value={f.access_level}
                      onChange={(e) => updateAccessLevel(f.field_name, e.target.value as SelfServiceAccessLevel)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                    >
                      <option value="hidden">Hidden</option>
                      <option value="view_only">View Only</option>
                      <option value="direct_edit">Direct Edit</option>
                      <option value="requires_approval">Requires Approval</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {fields.length > 0 && (
        <div className="flex">
          <button
            type="button"
            onClick={saveAll}
            disabled={saving}
            className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>
      )}

      {fields.length === 0 && !error && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
          <p className="text-sm text-gray-500">
            No self-service field configuration found.
          </p>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 7: Field Options (configurable dropdown values)
 * ──────────────────────────────────────────────────────────────────────────── */

const CATEGORY_LABELS: Record<string, string> = {
  employee_field: "Employee Fields",
  standard_field: "Standard Fields",
  custom_field: "Custom Fields",
};

function FieldOptionsTab() {
  const { lists, loading: listsLoading } = useDropdownLists();
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Option form state
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState<number | null>(null);
  const [optCode, setOptCode] = useState("");
  const [optLabel, setOptLabel] = useState("");
  const [optLabelEs, setOptLabelEs] = useState("");
  const [optRegionId, setOptRegionId] = useState("");
  const [optSortOrder, setOptSortOrder] = useState("0");
  const [optIsDefault, setOptIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  // New list form state
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListCode, setNewListCode] = useState("");
  const [newListName, setNewListName] = useState("");
  const [newListCategory, setNewListCategory] = useState("custom_field");
  const [newListDesc, setNewListDesc] = useState("");

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<Record<string, { new: number; updated: number; error?: string }> | null>(null);
  const [importing, setImporting] = useState(false);

  const selectedList = lists.find((l) => l.id === selectedListId) ?? null;

  // Group lists by category
  const grouped: Record<string, DropdownListItem[]> = {};
  for (const l of lists) {
    const cat = l.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(l);
  }
  const categoryOrder = ["employee_field", "standard_field", "custom_field"];
  const sortedCategories = categoryOrder.filter((c) => grouped[c]?.length);
  for (const c of Object.keys(grouped)) {
    if (!sortedCategories.includes(c)) sortedCategories.push(c);
  }

  const resetOptionForm = () => {
    setOptCode("");
    setOptLabel("");
    setOptLabelEs("");
    setOptRegionId("");
    setOptSortOrder("0");
    setOptIsDefault(false);
    setEditingOptionId(null);
    setShowOptionForm(false);
  };

  const openEditOption = (o: DropdownOptionItem) => {
    setEditingOptionId(o.id);
    setOptCode(o.code);
    setOptLabel(o.label);
    setOptLabelEs(o.label_es ?? "");
    setOptRegionId(o.region_id != null ? String(o.region_id) : "");
    setOptSortOrder(String(o.sort_order));
    setOptIsDefault(o.is_default);
    setShowOptionForm(true);
  };

  const submitOption = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedList) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body = {
        code: optCode,
        label: optLabel,
        label_es: optLabelEs || null,
        region_id: optRegionId ? Number(optRegionId) : null,
        sort_order: Number(optSortOrder) || 0,
        is_default: optIsDefault,
      };
      if (editingOptionId) {
        await api.put(`/api/dropdowns/options/${editingOptionId}`, body);
        setNotice("Option updated.");
      } else {
        await api.post(`/api/dropdowns/${selectedList.id}/options`, body);
        setNotice("Option added.");
      }
      resetOptionForm();
      invalidateDropdownCache();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const deactivateOption = async (optionId: number) => {
    if (!confirm("Deactivate this option?")) return;
    setError(null);
    try {
      await api.delete(`/api/dropdowns/options/${optionId}`);
      setNotice("Option deactivated.");
      invalidateDropdownCache();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  const submitNewList = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.post("/api/dropdowns", {
        code: newListCode,
        name: newListName,
        category: newListCategory,
        description: newListDesc || null,
      });
      setNewListCode("");
      setNewListName("");
      setNewListCategory("custom_field");
      setNewListDesc("");
      setShowNewListForm(false);
      setNotice("List created.");
      invalidateDropdownCache();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async (listId: number) => {
    if (!confirm("Delete this dropdown list and all its options?")) return;
    setError(null);
    try {
      await api.delete(`/api/dropdowns/${listId}`);
      setNotice("List deleted.");
      if (selectedListId === listId) setSelectedListId(null);
      invalidateDropdownCache();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  const exportSettings = async () => {
    try {
      const res = await fetch("/api/settings/export", { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pme_people_settings_export.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const previewImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await fetch("/api/settings/import", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Import preview failed");
      const data = await res.json();
      setImportPreview(data.preview);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await fetch("/api/settings/import/confirm", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Import failed");
      const data = await res.json();
      setNotice(`Import complete: ${data.new} new, ${data.updated} updated.`);
      setImportFile(null);
      setImportPreview(null);
      invalidateDropdownCache();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  if (listsLoading) return <p className="text-sm text-gray-500">Loading...</p>;

  const allOptions: DropdownOptionItem[] = selectedList
    ? (selectedList.options ?? []).sort((a, b) => a.sort_order - b.sort_order)
    : [];

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {notice && (
        <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setShowNewListForm(true)}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">
          Add New List
        </button>
        <button type="button" onClick={exportSettings}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
          Export XLSX
        </button>
        <label className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
          Import XLSX
          <input type="file" accept=".xlsx" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setImportFile(f);
              setImportPreview(null);
              e.target.value = "";
            }} />
        </label>
        {importFile && !importPreview && (
          <button type="button" onClick={previewImport} disabled={importing}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {importing ? "Previewing..." : `Preview "${importFile.name}"`}
          </button>
        )}
      </div>

      {/* Import preview */}
      {importPreview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-amber-900">Import Preview</h4>
          <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">List</th>
                  <th className="px-3 py-2">New Options</th>
                  <th className="px-3 py-2">Updated</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(importPreview).map(([code, info]) => (
                  <tr key={code}>
                    <td className="px-3 py-2 font-mono text-xs">{code}</td>
                    <td className="px-3 py-2">{info.new}</td>
                    <td className="px-3 py-2">{info.updated}</td>
                    <td className="px-3 py-2">
                      {info.error ? (
                        <span className="text-red-600">{info.error}</span>
                      ) : (
                        <span className="text-green-600">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={confirmImport} disabled={importing}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {importing ? "Importing..." : "Confirm Import"}
            </button>
            <button type="button" onClick={() => { setImportFile(null); setImportPreview(null); }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* New list form */}
      {showNewListForm && (
        <form onSubmit={submitNewList} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">New Dropdown List</h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Code</span>
              <input type="text" value={newListCode} onChange={(e) => setNewListCode(e.target.value)} required
                placeholder="e.g. custom_status" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Name</span>
              <input type="text" value={newListName} onChange={(e) => setNewListName(e.target.value)} required
                placeholder="e.g. Custom Status" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Category</span>
              <select value={newListCategory} onChange={(e) => setNewListCategory(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="employee_field">Employee Field</option>
                <option value="standard_field">Standard Field</option>
                <option value="custom_field">Custom Field</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-gray-700">Description</span>
              <input type="text" value={newListDesc} onChange={(e) => setNewListDesc(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Creating..." : "Create"}
            </button>
            <button type="button" onClick={() => setShowNewListForm(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Left sidebar — list of dropdown lists */}
        <div className="lg:col-span-1 space-y-3">
          {sortedCategories.map((cat) => (
            <div key={cat}>
              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {CATEGORY_LABELS[cat] ?? cat}
              </h5>
              <div className="space-y-0.5">
                {grouped[cat].map((l) => (
                  <button key={l.id} type="button" onClick={() => { setSelectedListId(l.id); resetOptionForm(); }}
                    className={[
                      "block w-full rounded-lg px-3 py-2 text-left text-sm",
                      selectedListId === l.id
                        ? "bg-pme-blue text-white font-semibold"
                        : "text-gray-700 hover:bg-gray-100",
                    ].join(" ")}>
                    {l.name}
                    <span className={[
                      "ml-1 text-xs",
                      selectedListId === l.id ? "text-blue-200" : "text-gray-400",
                    ].join(" ")}>
                      ({(l.options ?? []).length})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {lists.length === 0 && (
            <p className="text-sm text-gray-400">No dropdown lists configured.</p>
          )}
        </div>

        {/* Right panel — selected list's options */}
        <div className="lg:col-span-3">
          {!selectedList && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
              <p className="text-sm text-gray-500">Select a dropdown list from the left to view and manage its options.</p>
            </div>
          )}

          {selectedList && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedList.name}</h3>
                  <p className="text-xs text-gray-500">
                    Code: <span className="font-mono">{selectedList.code}</span>
                    {selectedList.description && <> — {selectedList.description}</>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { resetOptionForm(); setShowOptionForm(true); }}
                    className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">
                    Add Option
                  </button>
                  {!selectedList.is_system && (
                    <button type="button" onClick={() => deleteList(selectedList.id)}
                      className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
                      Delete List
                    </button>
                  )}
                </div>
              </div>

              {/* Options table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Code</th>
                      <th className="px-3 py-2">Label</th>
                      <th className="px-3 py-2">Spanish</th>
                      <th className="px-3 py-2">Region</th>
                      <th className="px-3 py-2">Order</th>
                      <th className="px-3 py-2">Default</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allOptions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                          No options. Click "Add Option" to create one.
                        </td>
                      </tr>
                    )}
                    {allOptions.map((o) => (
                      <tr key={o.id} className={o.is_active ? "" : "opacity-50"}>
                        <td className="px-3 py-2 font-mono text-xs">{o.code}</td>
                        <td className="px-3 py-2">{o.label}</td>
                        <td className="px-3 py-2 text-gray-500">{o.label_es || "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{o.region_id ?? "All"}</td>
                        <td className="px-3 py-2 text-gray-500">{o.sort_order}</td>
                        <td className="px-3 py-2">{o.is_default ? "Yes" : ""}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button type="button" onClick={() => openEditOption(o)}
                            className="mr-2 text-xs text-pme-blue hover:underline">
                            Edit
                          </button>
                          {o.is_active && (
                            <button type="button" onClick={() => void deactivateOption(o.id)}
                              className="text-xs text-red-600 hover:underline">
                              Deactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Option form */}
              {showOptionForm && (
                <form onSubmit={submitOption} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {editingOptionId ? "Edit Option" : "New Option"}
                  </h4>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <label className="block text-sm">
                      <span className="block text-xs font-medium text-gray-700">Code</span>
                      <input type="text" value={optCode} onChange={(e) => setOptCode(e.target.value)} required
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-xs font-medium text-gray-700">Label</span>
                      <input type="text" value={optLabel} onChange={(e) => setOptLabel(e.target.value)} required
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-xs font-medium text-gray-700">Spanish Label</span>
                      <input type="text" value={optLabelEs} onChange={(e) => setOptLabelEs(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-xs font-medium text-gray-700">Region ID</span>
                      <input type="number" value={optRegionId} onChange={(e) => setOptRegionId(e.target.value)}
                        placeholder="All regions" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-xs font-medium text-gray-700">Sort Order</span>
                      <input type="number" value={optSortOrder} onChange={(e) => setOptSortOrder(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </label>
                    <label className="flex items-center gap-2 text-sm pt-5">
                      <input type="checkbox" checked={optIsDefault} onChange={(e) => setOptIsDefault(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue" />
                      <span className="text-gray-700">Default</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={saving}
                      className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                      {saving ? "Saving..." : editingOptionId ? "Update" : "Add"}
                    </button>
                    <button type="button" onClick={resetOptionForm}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab: Directory Privacy
 * ──────────────────────────────────────────────────────────────────────────── */

interface DirectoryFieldConfigItem {
  id: number;
  field_name: string;
  field_label: string;
  visible_in_directory: boolean;
  visible_in_org_chart: boolean;
  sort_order: number;
}

const DIR_CATEGORY_ORDER = ["Personal", "Contact", "Address", "Employment", "Compensation", "Emergency Contact"] as const;

function dirFieldCategory(sortOrder: number): string {
  if (sortOrder < 20) return "Personal";
  if (sortOrder < 30) return "Contact";
  if (sortOrder < 40) return "Address";
  if (sortOrder < 50) return "Employment";
  if (sortOrder < 70) return "Compensation";
  return "Emergency Contact";
}

function DirectoryTab() {
  const [fields, setFields] = useState<DirectoryFieldConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<DirectoryFieldConfigItem[]>("/api/settings/directory")
      .then((data) => setFields(data))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id: number, key: "visible_in_directory" | "visible_in_org_chart") => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: !f[key] } : f)),
    );
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put("/api/settings/directory", {
        fields: fields.map((f) => ({
          id: f.id,
          visible_in_directory: f.visible_in_directory,
          visible_in_org_chart: f.visible_in_org_chart,
        })),
      });
      setNotice("Directory visibility settings saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  const grouped = new Map<string, DirectoryFieldConfigItem[]>();
  for (const cat of DIR_CATEGORY_ORDER) grouped.set(cat, []);
  for (const f of fields) {
    const cat = dirFieldCategory(f.sort_order);
    const list = grouped.get(cat);
    if (list) list.push(f);
    else grouped.set(cat, [f]);
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Directory &amp; Org Chart Privacy</h3>
        <p className="text-xs text-gray-500">
          Control which fields are visible to non-admin employees in the directory and org chart.
          Admins always see all fields.
        </p>
      </div>

      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {notice && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>}

      {Array.from(grouped.entries())
        .filter(([, items]) => items.length > 0)
        .map(([category, items]) => (
          <div key={category}>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">{category}</h4>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500">
                    <th className="px-3 py-2">Field</th>
                    <th className="px-3 py-2 text-center">Directory</th>
                    <th className="px-3 py-2 text-center">Org Chart</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => (
                    <tr key={f.id} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{f.field_label}</td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={f.visible_in_directory}
                          onChange={() => toggle(f.id, "visible_in_directory")}
                          className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={f.visible_in_org_chart}
                          onChange={() => toggle(f.id, "visible_in_org_chart")}
                          className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab: Custom Fields
 * ──────────────────────────────────────────────────────────────────────────── */

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "yes_no", label: "Yes/No" },
  { value: "currency", label: "Currency" },
  { value: "file", label: "File Upload" },
  { value: "calculation", label: "Calculation" },
  { value: "multi_select", label: "Multi-select" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "URL" },
];

const PROFILE_TABS = [
  { value: "profile", label: "Profile" },
  { value: "documents", label: "Documents" },
  { value: "training", label: "Training" },
  { value: "performance", label: "Performance" },
];

interface CustomFieldDef {
  id: number;
  name: string;
  code: string;
  description: string | null;
  field_type: string;
  field_category: string;
  dropdown_list_id: number | null;
  calculation_formula: string | null;
  is_required: boolean;
  is_active: boolean;
  show_on_profile: boolean;
  profile_tab: string;
  sort_order: number;
  default_value: string | null;
  validation_regex: string | null;
  min_value: number | null;
  max_value: number | null;
  visible_to_employee: boolean;
  editable_by_employee: boolean;
  region_ids: number[] | null;
}

function CustomFieldsTab() {
  const [fields, setFields] = useState<CustomFieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<CustomFieldDef | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formType, setFormType] = useState("text");
  const [formCategory, setFormCategory] = useState("Custom");
  const [formDropdownListId, setFormDropdownListId] = useState<number | null>(null);
  const [formFormula, setFormFormula] = useState("");
  const [formRequired, setFormRequired] = useState(false);
  const [formShowOnProfile, setFormShowOnProfile] = useState(true);
  const [formProfileTab, setFormProfileTab] = useState("profile");
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formDefaultValue, setFormDefaultValue] = useState("");
  const [formRegex, setFormRegex] = useState("");
  const [formMin, setFormMin] = useState("");
  const [formMax, setFormMax] = useState("");
  const [formVisibleToEmployee, setFormVisibleToEmployee] = useState(false);
  const [formEditableByEmployee, setFormEditableByEmployee] = useState(false);
  const [formRegionIds, setFormRegionIds] = useState<number[]>([]);
  const [formAllRegions, setFormAllRegions] = useState(true);
  const [regions, setRegions] = useState<Region[]>([]);
  const [dropdownLists, setDropdownLists] = useState<DropdownListItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defs, regs, lists] = await Promise.all([
        api.get<CustomFieldDef[]>("/api/custom-fields"),
        api.get<Region[]>("/api/regions"),
        api.get<DropdownListItem[]>("/api/dropdowns"),
      ]);
      setFields(defs);
      setRegions(regs);
      setDropdownLists(lists);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  const resetForm = () => {
    setFormName(""); setFormCode(""); setFormDesc(""); setFormType("text");
    setFormCategory("Custom"); setFormDropdownListId(null); setFormFormula("");
    setFormRequired(false); setFormShowOnProfile(true); setFormProfileTab("profile");
    setFormSortOrder(0); setFormDefaultValue(""); setFormRegex("");
    setFormMin(""); setFormMax(""); setFormVisibleToEmployee(false);
    setFormEditableByEmployee(false); setFormRegionIds([]); setFormAllRegions(true);
  };

  const startCreate = () => {
    resetForm();
    setEditing(null);
    setCreating(true);
    setError(null); setNotice(null);
  };

  const startEdit = (f: CustomFieldDef) => {
    setCreating(false);
    setEditing(f);
    setFormName(f.name); setFormCode(f.code); setFormDesc(f.description ?? "");
    setFormType(f.field_type); setFormCategory(f.field_category);
    setFormDropdownListId(f.dropdown_list_id); setFormFormula(f.calculation_formula ?? "");
    setFormRequired(f.is_required); setFormShowOnProfile(f.show_on_profile);
    setFormProfileTab(f.profile_tab); setFormSortOrder(f.sort_order);
    setFormDefaultValue(f.default_value ?? ""); setFormRegex(f.validation_regex ?? "");
    setFormMin(f.min_value != null ? String(f.min_value) : "");
    setFormMax(f.max_value != null ? String(f.max_value) : "");
    setFormVisibleToEmployee(f.visible_to_employee);
    setFormEditableByEmployee(f.editable_by_employee);
    setFormRegionIds(f.region_ids ?? []); setFormAllRegions(!f.region_ids || f.region_ids.length === 0);
    setError(null); setNotice(null);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null); setNotice(null);
    const body = {
      name: formName,
      code: formCode || slugify(formName),
      description: formDesc || null,
      field_type: formType,
      field_category: formCategory,
      dropdown_list_id: (formType === "dropdown" || formType === "multi_select") ? formDropdownListId : null,
      calculation_formula: formType === "calculation" ? formFormula : null,
      is_required: formRequired,
      show_on_profile: formShowOnProfile,
      profile_tab: formProfileTab,
      sort_order: formSortOrder,
      default_value: formDefaultValue || null,
      validation_regex: formRegex || null,
      min_value: formMin ? Number(formMin) : null,
      max_value: formMax ? Number(formMax) : null,
      visible_to_employee: formVisibleToEmployee,
      editable_by_employee: formEditableByEmployee,
      region_ids: formAllRegions ? null : formRegionIds,
    };
    try {
      if (editing) {
        await api.put(`/api/custom-fields/${editing.id}`, body);
        setNotice("Custom field updated.");
      } else {
        await api.post("/api/custom-fields", body);
        setNotice("Custom field created.");
      }
      setCreating(false); setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const deleteField = async (id: number) => {
    if (!confirm("Deactivate this custom field?")) return;
    try {
      await api.delete(`/api/custom-fields/${id}`);
      setNotice("Custom field deactivated.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  const grouped = fields.reduce<Record<string, CustomFieldDef[]>>((acc, f) => {
    (acc[f.field_category] ??= []).push(f);
    return acc;
  }, {});

  const showForm = creating || editing !== null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Custom Fields</h3>
          <p className="text-xs text-gray-500">Create custom data fields that appear on employee profiles.</p>
        </div>
        <button type="button" onClick={startCreate} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">
          Add Custom Field
        </button>
      </div>

      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {notice && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>}

      <div className="flex gap-6">
        <div className="w-[55%] shrink-0 space-y-4">
          {Object.entries(grouped).map(([cat, defs]) => (
            <div key={cat}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{cat}</h4>
              <div className="space-y-1">
                {defs.map((f) => (
                  <div
                    key={f.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${editing?.id === f.id ? "border-l-4 border-l-pme-blue bg-blue-50/50" : "border-gray-200 bg-white hover:bg-gray-50"}`}
                  >
                    <div className="cursor-pointer flex-1" onClick={() => startEdit(f)}>
                      <span className="font-medium text-gray-900">{f.name}</span>
                      <span className="ml-2 text-xs text-gray-400">{f.field_type}</span>
                      {f.is_required && <span className="ml-1 text-xs text-red-500">*</span>}
                    </div>
                    <button type="button" onClick={() => deleteField(f.id)} className="text-xs text-red-500 hover:text-red-700">
                      Deactivate
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {fields.length === 0 && (
            <p className="text-sm text-gray-400">No custom fields defined yet.</p>
          )}
        </div>

        {showForm && (
          <div className="min-w-0 flex-1">
            <form onSubmit={submitForm} className="sticky top-4 space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-900">{creating ? "New Custom Field" : `Edit: ${editing?.name}`}</h4>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Name</span>
                  <input type="text" value={formName} onChange={(e) => { setFormName(e.target.value); if (creating) setFormCode(slugify(e.target.value)); }} required className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Code</span>
                  <input type="text" value={formCode} onChange={(e) => setFormCode(e.target.value)} required className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-xs font-medium text-gray-700">Description</span>
                <input type="text" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Field Type</span>
                  <select value={formType} onChange={(e) => setFormType(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Category</span>
                  <input type="text" value={formCategory} onChange={(e) => setFormCategory(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Custom" />
                </label>
              </div>

              {(formType === "dropdown" || formType === "multi_select") && (
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Linked Dropdown List</span>
                  <select value={formDropdownListId ?? ""} onChange={(e) => setFormDropdownListId(e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    <option value="">-- Select list --</option>
                    {dropdownLists.map((dl) => <option key={dl.id} value={dl.id}>{dl.name}</option>)}
                  </select>
                </label>
              )}

              {formType === "calculation" && (
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Formula</span>
                  <input type="text" value={formFormula} onChange={(e) => setFormFormula(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" placeholder="years_since(start_date)" />
                  <p className="mt-1 text-[11px] text-gray-400">Functions: days_until(), days_since(), years_since(), months_since(). Use field codes and basic math (+, -, *, /).</p>
                </label>
              )}

              {formType === "number" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-xs font-medium text-gray-700">Min Value</span>
                    <input type="number" value={formMin} onChange={(e) => setFormMin(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-xs font-medium text-gray-700">Max Value</span>
                    <input type="number" value={formMax} onChange={(e) => setFormMax(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </label>
                </div>
              )}

              {formType === "text" && (
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Validation Regex</span>
                  <input type="text" value={formRegex} onChange={(e) => setFormRegex(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" placeholder="Optional" />
                </label>
              )}

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Profile Tab</span>
                  <select value={formProfileTab} onChange={(e) => setFormProfileTab(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {PROFILE_TABS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-medium text-gray-700">Sort Order</span>
                  <input type="number" value={formSortOrder} onChange={(e) => setFormSortOrder(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-xs font-medium text-gray-700">Default Value</span>
                <input type="text" value={formDefaultValue} onChange={(e) => setFormDefaultValue(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </label>

              <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                <h5 className="text-xs font-semibold text-gray-700">Visibility & Access</h5>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formRequired} onChange={(e) => setFormRequired(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                  <span className="text-gray-700">Required field</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formShowOnProfile} onChange={(e) => setFormShowOnProfile(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                  <span className="text-gray-700">Show on admin employee profile</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formVisibleToEmployee} onChange={(e) => setFormVisibleToEmployee(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                  <span className="text-gray-700">Visible to employee on /me</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formEditableByEmployee} onChange={(e) => setFormEditableByEmployee(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                  <span className="text-gray-700">Editable by employee</span>
                </label>
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-semibold text-gray-700">Region Filter</h5>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formAllRegions} onChange={(e) => { setFormAllRegions(e.target.checked); if (e.target.checked) setFormRegionIds([]); }} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                  <span className="text-gray-700">All regions</span>
                </label>
                {!formAllRegions && (
                  <div className="flex flex-wrap gap-2 pl-6">
                    {regions.map((r) => (
                      <label key={r.id} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={formRegionIds.includes(r.id)} onChange={() => setFormRegionIds((prev) => prev.includes(r.id) ? prev.filter((x) => x !== r.id) : [...prev, r.id])} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                        <span className="text-gray-700">{r.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button type="submit" disabled={saving} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                  {saving ? "Saving..." : creating ? "Create" : "Save"}
                </button>
                <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="text-sm text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 8: Notifications
 * ──────────────────────────────────────────────────────────────────────────── */

interface NotifGroupMember {
  id?: number;
  access_level_id: number | null;
  employee_id: number | null;
  scope: "site" | "region" | "global";
  is_enabled: boolean;
}
interface NotifGroup {
  id: number;
  name: string;
  description: string | null;
  fallback_employee_id: number | null;
  members: NotifGroupMember[];
}
interface NotifType {
  code: string;
  label: string;
  description: string | null;
  category: string;
  group_id: number | null;
  notify_subject: boolean;
  notify_subject_manager: boolean;
  subject_lockable: boolean;
  is_active: boolean;
}
interface NotifSettings { groups: NotifGroup[]; types: NotifType[]; }
interface AccessLevelLite { id: number; name: string; }

interface EmailLogEntry {
  id: number;
  sent_at: string | null;
  recipient_email: string;
  subject: string;
  notification_type: string | null;
  subject_employee_name: string | null;
  trigger_event: string | null;
  outcome: string;
  error_detail: string | null;
}

function EmailSendLog({ paused }: { paused: boolean }) {
  const [entries, setEntries] = useState<EmailLogEntry[]>([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [since, setSince] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (typeFilter) qs.set("notification_type", typeFilter);
      if (recipientFilter) qs.set("recipient", recipientFilter);
      if (since) qs.set("since", since);
      const d = await api.get<{ entries: EmailLogEntry[] }>(`/api/settings/email/log?${qs.toString()}`);
      setEntries(d?.entries ?? []);
    } catch { /* non-critical */ } finally { setLoading(false); }
  }, [typeFilter, recipientFilter, since]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      {paused && (
        <div className="mb-4">
          <EmailPausedBanner />
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-sm font-semibold text-gray-900"
      >
        {open ? "▾" : "▸"} Send Log
      </button>
      <p className="mt-1 text-xs text-gray-500">
        Every outbound email, including failures. Retained for 24 months.
      </p>
      {open && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <input value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              placeholder="Type (e.g. review.action.pip)"
              className="rounded border border-gray-300 px-2 py-1 text-xs" />
            <input value={recipientFilter} onChange={(e) => setRecipientFilter(e.target.value)}
              placeholder="Recipient contains…"
              className="rounded border border-gray-300 px-2 py-1 text-xs" />
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-xs" />
            <button type="button" onClick={() => void load()}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
              Apply
            </button>
          </div>
          <div className="mt-3 overflow-x-auto">
            {loading ? <p className="text-xs text-gray-400">Loading…</p> : entries.length === 0 ? (
              <p className="text-xs text-gray-400">No emails logged for this filter.</p>
            ) : (
              <table className="w-full min-w-[760px] text-xs">
                <thead className="text-left text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-2">Sent</th>
                    <th className="px-2 py-2">Recipient</th>
                    <th className="px-2 py-2">Subject</th>
                    <th className="px-2 py-2">Type</th>
                    <th className="px-2 py-2">About</th>
                    <th className="px-2 py-2">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-gray-100">
                      <td className="py-2 pr-2 text-gray-500">
                        {e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-800">{e.recipient_email}</td>
                      <td className="px-2 py-2 text-gray-700">{e.subject}</td>
                      <td className="px-2 py-2 text-gray-500">{e.notification_type ?? "—"}</td>
                      <td className="px-2 py-2 text-gray-500">{e.subject_employee_name ?? "—"}</td>
                      <td className="px-2 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          e.outcome === "sent" ? "bg-green-50 text-green-700"
                          : e.outcome === "failed" ? "bg-red-50 text-red-700"
                          : e.outcome === "skipped_paused" ? "bg-amber-50 text-amber-800"
                          : "bg-gray-100 text-gray-600"}`}
                          title={e.error_detail ?? undefined}>
                          {e.outcome}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Read-only routing summary for a Review Action.
 * The notify_manager / notify_hr / notify_employee columns on
 * ActionTriggerMapping were retired — resolve_recipients ignores them — so the
 * old toggles were dead controls. This shows the real routing instead.
 */
function ActionRoutingSummary({ actionCode }: { actionCode: string }) {
  const [data, setData] = useState<NotifSettings | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await api.get<NotifSettings>("/api/settings/notifications");
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const typeCode = actionCode ? `review.action.${actionCode}` : "";
  const nt = data?.types.find((t) => t.code === typeCode);
  const groupName = nt?.group_id
    ? data?.groups.find((g) => g.id === nt.group_id)?.name ?? "—"
    : "None";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-medium text-gray-700">
        Recipients are configured in{" "}
        <a href="/admin/settings?tab=notifications" className="text-pme-blue underline">
          Settings &gt; Notifications
        </a>
      </p>
      {failed ? (
        <p className="mt-1 text-xs text-gray-400">Could not load current routing.</p>
      ) : !actionCode ? (
        <p className="mt-1 text-xs text-gray-500">
          Enter an action code to see its routing.
        </p>
      ) : !nt ? (
        <p className="mt-1 text-xs text-amber-700">
          No notification type <code>{typeCode}</code> exists yet — this action will not
          send email until one is created.
        </p>
      ) : (
        <ul className="mt-1.5 space-y-0.5 text-xs text-gray-600">
          <li>
            Employee&apos;s manager:{" "}
            <span className="font-medium text-gray-800">
              {nt.notify_subject_manager ? "Yes" : "No"}
            </span>
          </li>
          <li>
            Employee:{" "}
            <span className="font-medium text-gray-800">
              {nt.subject_lockable
                ? "Never (confidential — cannot be enabled)"
                : nt.notify_subject ? "Yes" : "No"}
            </span>
          </li>
          <li>
            Group: <span className="font-medium text-gray-800">{groupName}</span>
          </li>
          {!nt.is_active && (
            <li className="text-amber-700">This notification type is inactive — nothing is sent.</li>
          )}
        </ul>
      )}
    </div>
  );
}

function NotificationsTab() {
  const [data, setData] = useState<NotifSettings | null>(null);
  const [levels, setLevels] = useState<AccessLevelLite[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, ls] = await Promise.all([
        api.get<NotifSettings>("/api/settings/notifications"),
        api.get<AccessLevelLite[]>("/api/access-levels"),
      ]);
      setData(d);
      setLevels(ls ?? []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load notification settings");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const patchType = (code: string, patch: Partial<NotifType>) => {
    setData((prev) => prev && {
      ...prev,
      types: prev.types.map((t) => (t.code === code ? { ...t, ...patch } : t)),
    });
  };

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [newGroupFallback, setNewGroupFallback] = useState<number | null>(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [busyGroupId, setBusyGroupId] = useState<number | null>(null);

  const createGroup = async () => {
    if (!newGroupName.trim()) { setError("Group name is required."); return; }
    setSaving(true); setError(null); setNotice(null);
    try {
      await api.post("/api/settings/notifications/groups", {
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || null,
        fallback_employee_id: newGroupFallback,
      });
      setNewGroupName(""); setNewGroupDesc(""); setNewGroupFallback(null);
      setShowAddGroup(false);
      await load();
      setNotice("Group created.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create group");
    } finally { setSaving(false); }
  };

  const deleteGroup = async (g: NotifGroup) => {
    if (!window.confirm(`Delete the group "${g.name}"? Its members will be removed.`)) return;
    setBusyGroupId(g.id); setError(null); setNotice(null);
    try {
      await api.delete(`/api/settings/notifications/groups/${g.id}`);
      await load();
      setNotice(`Group "${g.name}" deleted.`);
    } catch (e) {
      // 409 names the notification types still routing to this group.
      setError(e instanceof ApiError ? e.message : "Could not delete group");
    } finally { setBusyGroupId(null); }
  };

  /** Save one group's members + fallback without touching anything else. */
  const saveGroup = async (g: NotifGroup) => {
    const incomplete = g.members.some((m) => m.access_level_id == null && m.employee_id == null);
    if (incomplete) {
      setError(`Group "${g.name}": every member needs an access level or a named person.`);
      return;
    }
    setBusyGroupId(g.id); setError(null); setNotice(null);
    try {
      const fresh = await api.put<NotifSettings>("/api/settings/notifications", {
        groups: [{
          id: g.id,
          fallback_employee_id: g.fallback_employee_id,
          members: g.members.map((m) => ({
            access_level_id: m.access_level_id,
            employee_id: m.employee_id,
            scope: m.scope,
            is_enabled: m.is_enabled,
          })),
        }],
      });
      setData(fresh);
      setNotice(`"${g.name}" saved.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally { setBusyGroupId(null); }
  };

  const patchGroup = (id: number, patch: Partial<NotifGroup>) => {
    setData((prev) => prev && {
      ...prev,
      groups: prev.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const fresh = await api.put<NotifSettings>("/api/settings/notifications", {
        types: data.types.map((t) => ({
          code: t.code,
          group_id: t.group_id,
          notify_subject: t.notify_subject,
          notify_subject_manager: t.notify_subject_manager,
          is_active: t.is_active,
        })),
        // Groups are saved individually via "Save group" so an unfinished
        // member row cannot block saving the type routing.
      });
      setData(fresh);
      setNotice("Notification settings saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return <p className="text-sm text-gray-500">Loading...</p>;

  const byCategory: Record<string, NotifType[]> = {};
  for (const t of data?.types ?? []) {
    (byCategory[t.category] ??= []).push(t);
  }

  return (
    <div className="space-y-5">
      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {notice && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <p className="text-xs text-blue-800">
          Recipients are only ever the people you tick here. Nobody receives a notification
          because of their rank or admin status, and Super Admins are never included by default.
          Role members are scoped to the subject employee's site unless you widen them.
        </p>
      </div>

      {/* Groups */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Recipient Groups</h3>
          <button
            type="button"
            onClick={() => setShowAddGroup((v) => !v)}
            className="rounded border border-blue-300 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            {showAddGroup ? "Cancel" : "+ Add Group"}
          </button>
        </div>

        {showAddGroup && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block text-xs">
                <span className="font-medium text-gray-700">Name</span>
                <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g. Regional HR" className="input mt-1 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="font-medium text-gray-700">Description (optional)</span>
                <input value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)}
                  placeholder="What this group is for" className="input mt-1 text-sm" />
              </label>
            </div>
            <label className="block text-xs">
              <span className="font-medium text-gray-700">Fallback recipient (optional)</span>
              <div className="mt-1 max-w-sm">
                <EmployeeSearch value={newGroupFallback} onChange={setNewGroupFallback}
                  placeholder="Used only if the group resolves to nobody" className="input text-xs" />
              </div>
            </label>
            <button type="button" onClick={() => void createGroup()} disabled={saving}
              className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
              {saving ? "Creating…" : "Create group"}
            </button>
          </div>
        )}

        {(data?.groups ?? []).map((g) => (
          <div key={g.id} className="rounded-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{g.name}</p>
                {g.description && <p className="text-xs text-gray-500">{g.description}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => patchGroup(g.id, {
                    members: [...g.members, { access_level_id: null, employee_id: null, scope: "site", is_enabled: true }],
                  })}
                  className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                >
                  + Add member
                </button>
                <button
                  type="button"
                  onClick={() => void saveGroup(g)}
                  disabled={busyGroupId === g.id}
                  className="rounded bg-pme-blue px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {busyGroupId === g.id ? "Saving…" : "Save group"}
                </button>
                <button
                  type="button"
                  onClick={() => void deleteGroup(g)}
                  disabled={busyGroupId === g.id}
                  className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  title="Only possible when no notification type routes to this group"
                >
                  Delete
                </button>
              </div>
            </div>

            {g.members.length === 0 ? (
              <p className="mt-2 text-xs text-amber-700">
                No members — this group currently sends to nobody.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {g.members.map((m, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1.5">
                    <select
                      value={m.access_level_id ?? ""}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        patchGroup(g.id, {
                          members: g.members.map((x, i) =>
                            i === idx ? { ...x, access_level_id: v, employee_id: v ? null : x.employee_id } : x),
                        });
                      }}
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="">— Access level —</option>
                      {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>

                    <div className="min-w-[200px] flex-1">
                      <EmployeeSearch
                        value={m.employee_id}
                        onChange={(id) => patchGroup(g.id, {
                          members: g.members.map((x, i) =>
                            i === idx ? { ...x, employee_id: id, access_level_id: id ? null : x.access_level_id } : x),
                        })}
                        placeholder="…or a named person"
                        className="input text-xs"
                      />
                    </div>

                    <select
                      value={m.scope}
                      onChange={(e) => patchGroup(g.id, {
                        members: g.members.map((x, i) =>
                          i === idx ? { ...x, scope: e.target.value as NotifGroupMember["scope"] } : x),
                      })}
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                      title="Site: same site as the employee. Region: same region. Global: always."
                    >
                      <option value="site">Same site</option>
                      <option value="region">Same region</option>
                      <option value="global">Global</option>
                    </select>

                    <label className="flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={m.is_enabled}
                        onChange={(e) => patchGroup(g.id, {
                          members: g.members.map((x, i) => i === idx ? { ...x, is_enabled: e.target.checked } : x),
                        })}
                        className="h-3.5 w-3.5 rounded border-gray-300"
                      />
                      Enabled
                    </label>

                    <button
                      type="button"
                      onClick={() => patchGroup(g.id, { members: g.members.filter((_, i) => i !== idx) })}
                      className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3">
              <span className="text-xs font-medium text-gray-600">Fallback recipient (used only if the group resolves to nobody)</span>
              <div className="mt-1 max-w-sm">
                <EmployeeSearch
                  value={g.fallback_employee_id}
                  onChange={(id) => patchGroup(g.id, { fallback_employee_id: id })}
                  placeholder="No fallback"
                  className="input text-xs"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Types */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Notification Types</h3>
        {Object.entries(byCategory).map(([cat, types]) => (
          <div key={cat}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{cat}</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead className="text-left text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-2">Notification</th>
                    <th className="px-2 py-2">Employee's manager</th>
                    <th className="px-2 py-2">Employee</th>
                    <th className="px-2 py-2">Group</th>
                    <th className="px-2 py-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((t) => (
                    <tr key={t.code} className="border-b border-gray-100">
                      <td className="py-2 pr-2">
                        <p className="font-medium text-gray-800">{t.label}</p>
                        {t.description && <p className="text-[11px] text-gray-500">{t.description}</p>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={t.notify_subject_manager}
                          onChange={(e) => patchType(t.code, { notify_subject_manager: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {t.subject_lockable ? (
                          <span
                            className="cursor-help text-[11px] font-medium text-gray-400"
                            title="This notification is about the employee and can never be sent to them."
                          >
                            Never
                          </span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={t.notify_subject}
                            onChange={(e) => patchType(t.code, { notify_subject: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-gray-300"
                          />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={t.group_id ?? ""}
                          onChange={(e) => patchType(t.code, { group_id: e.target.value ? Number(e.target.value) : null })}
                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                        >
                          <option value="">None</option>
                          {(data?.groups ?? []).map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={t.is_active}
                          onChange={(e) => patchType(t.code, { is_active: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-gray-300"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save notification settings"}
      </button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab: Review Cycles
 * ──────────────────────────────────────────────────────────────────────────── */

interface ReviewCycleSetting {
  id: number;
  name: string;
  review_period_start: string;
  review_period_end: string;
  schedule_type: string;
  schedule_month: number | null;
  schedule_day: number | null;
  scope_type: string;
  scope_id: number | null;
  deadline_self_days: number;
  deadline_peer_days: number;
  deadline_manager_days: number;
  status: string;
  total_reviews: number;
  completed_reviews: number;
  created_at: string;
}

function ReviewCyclesTab() {
  const [cycles, setCycles] = useState<ReviewCycleSetting[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [triggeringId, setTriggeringId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try { setCycles(await api.get("/api/reviews/cycles")); } catch (err) {
      if (err instanceof ApiError) console.error(err.message);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);


  const trigger = async (id: number) => {
    setTriggeringId(id);
    try {
      await api.post(`/api/reviews/cycles/${id}/trigger`, {});
      void load();
    } catch {} finally { setTriggeringId(null); }
  };

  const statusColor = (s: string) => {
    if (s === "active" || s === "in_progress") return "bg-green-50 text-green-700";
    if (s === "completed") return "bg-blue-50 text-blue-700";
    if (s === "draft") return "bg-yellow-50 text-yellow-700";
    return "bg-gray-100 text-gray-500";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Configure review cycles for performance reviews.</p>
        <button type="button" onClick={() => setShowForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Create Review Cycle</button>
      </div>

      {showForm && (
        <CycleForm onSaved={() => { setShowForm(false); void load(); }} onCancel={() => setShowForm(false)} />
      )}

      {cycles.map((c) => {
        const pct = c.total_reviews > 0 ? Math.round((c.completed_reviews / c.total_reviews) * 100) : 0;
        return (
          <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                <p className="text-xs text-gray-500">
                  {c.schedule_type} &middot; {c.scope_type}{c.scope_id ? ` (#${c.scope_id})` : ""}
                  {c.review_period_start && ` &middot; ${c.review_period_start} to ${c.review_period_end}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" disabled={triggeringId === c.id} onClick={() => trigger(c.id)} className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-60">
                  {triggeringId === c.id ? "Triggering..." : "Trigger Now"}
                </button>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusColor(c.status)}`}>
                  {c.status}
                </span>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{c.completed_reviews} / {c.total_reviews} reviews completed</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-pme-blue transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })}
      {cycles.length === 0 && !showForm && <p className="text-sm text-gray-400">No review cycles configured.</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab: Feature Toggles
 * ──────────────────────────────────────────────────────────────────────────── */

const FEATURE_META: Record<string, { label: string; description: string }> = {
  recognition_enabled: { label: "Recognition", description: "Allow employees to give recognition to colleagues" },
  social_feed_enabled: { label: "Social Feed", description: "Enable the social feed with posts, reactions, and comments" },
  surveys_enabled: { label: "Surveys", description: "Enable employee satisfaction and wellbeing surveys" },
  expenses_enabled: { label: "Expenses", description: "Allow employees to submit and track expense claims" },
  "360_reviews_enabled": { label: "Appraisals", description: "Turn on yearly appraisals (self-assessment, colleague feedback and manager review)" },
  birthday_celebrations_enabled: { label: "Birthday Celebrations", description: "Show birthday badges in directory and auto-post celebrations in social feed" },
  anniversary_celebrations_enabled: { label: "Anniversary Celebrations", description: "Show work anniversary badges in directory and auto-post celebrations in social feed" },
  custom_fields_enabled: { label: "Custom Fields", description: "Allow admins to create custom employee data fields" },
  legacy_reviews_enabled: { label: "Legacy Reviews", description: "Show the old performance review system tab in Admin Performance" },
  goals_tracking_enabled: { label: "Objectives", description: "Show the Objectives tab in Admin Performance (everyone's agreed appraisal objectives and progress)" },
  training_enabled: { label: "Training", description: "Show Training to employees (menu, profile tab and home reminders). Admin → Training stays available so courses can be set up first" },
  org_chart_enabled: { label: "Org Chart", description: "Show the org chart to employees in the More menu" },
};

/* ────────────────────────────────────────────────────────────────────────────
 * Review Actions Tab
 * ──────────────────────────────────────────────────────────────────────────── */

interface ActionTriggerMapping {
  id: number;
  action_code: string;
  action_label: string;
  trigger_type: string;
  task_title_template: string | null;
  task_description_template: string | null;
  default_due_days: number;
  default_priority: string;
  action_url_template: string | null;
  notify_manager: boolean;
  notify_hr: boolean;
  notify_employee: boolean;
  auto_create_disciplinary: boolean;
  is_system: boolean;
  is_active: boolean;
}

const TRIGGER_TYPES = [
  { value: "create_task_and_email", label: "Create Task + Email" },
  { value: "create_task", label: "Create Task Only" },
  { value: "send_email", label: "Send Email Only" },
  { value: "prompt_only", label: "Prompt Only (No Automation)" },
];

const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];

function ReviewActionsTab() {
  const [mappings, setMappings] = useState<ActionTriggerMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ActionTriggerMapping | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const [form, setForm] = useState({
    action_code: "",
    action_label: "",
    trigger_type: "create_task_and_email",
    task_title_template: "",
    task_description_template: "",
    default_due_days: 30,
    default_priority: "medium",
    action_url_template: "",
    notify_manager: true,
    notify_hr: true,
    notify_employee: false,
    auto_create_disciplinary: false,
    is_active: true,
  });

  const load = useCallback(async () => {
    try {
      setMappings(await api.get<ActionTriggerMapping[]>("/api/settings/action-triggers"));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (m: ActionTriggerMapping) => {
    setEditing(m);
    setCreating(false);
    setForm({
      action_code: m.action_code,
      action_label: m.action_label,
      trigger_type: m.trigger_type,
      task_title_template: m.task_title_template || "",
      task_description_template: m.task_description_template || "",
      default_due_days: m.default_due_days,
      default_priority: m.default_priority,
      action_url_template: m.action_url_template || "",
      notify_manager: m.notify_manager,
      notify_hr: m.notify_hr,
      notify_employee: m.notify_employee,
      auto_create_disciplinary: m.auto_create_disciplinary,
      is_active: m.is_active,
    });
  };

  const startCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm({
      action_code: "",
      action_label: "",
      trigger_type: "create_task_and_email",
      task_title_template: "",
      task_description_template: "",
      default_due_days: 30,
      default_priority: "medium",
      action_url_template: "",
      notify_manager: true,
      notify_hr: true,
      notify_employee: false,
      auto_create_disciplinary: false,
      is_active: true,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/settings/action-triggers/${editing.id}`, form);
        setToast("Mapping updated");
      } else {
        await api.post("/api/settings/action-triggers", form);
        setToast("Mapping created");
      }
      setEditing(null);
      setCreating(false);
      void load();
    } catch (err) {
      if (err instanceof ApiError) setToast(err.message);
    }
    setSaving(false);
  };

  const remove = async (m: ActionTriggerMapping) => {
    if (!confirm(`Delete "${m.action_label}"?`)) return;
    try {
      await api.delete(`/api/settings/action-triggers/${m.id}`);
      setToast("Mapping deleted");
      void load();
    } catch (err) {
      if (err instanceof ApiError) setToast(err.message);
    }
  };

  if (loading) return <p className="py-8 text-center text-sm text-gray-400">Loading...</p>;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Review Actions</h2>
          <p className="text-xs text-gray-500 mt-0.5">Configure what happens when a manager recommends actions in an appraisal.</p>
        </div>
        <button type="button" onClick={startCreate} className="rounded-lg bg-pme-blue px-3 py-2 text-xs font-semibold text-white hover:bg-pme-blue/90">
          + Add Action
        </button>
      </div>

      <div className="space-y-2 mb-6">
        {mappings.map((m) => (
          <div key={m.id} className={`rounded-lg border p-3 ${m.is_active ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-gray-900">{m.action_label}</span>
                {m.is_system && <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">System</span>}
                {!m.is_active && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">Inactive</span>}
                <p className="text-xs text-gray-500 mt-0.5">
                  {m.action_code} · {TRIGGER_TYPES.find((t) => t.value === m.trigger_type)?.label ?? m.trigger_type} · {m.default_due_days}d · {m.default_priority}
                </p>
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => startEdit(m)} className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
                  Edit
                </button>
                {!m.is_system && (
                  <button type="button" onClick={() => void remove(m)} className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50">
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {(editing || creating) && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{editing ? `Edit: ${editing.action_label}` : "New Action Mapping"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Action Code</label>
              <input
                type="text"
                value={form.action_code}
                disabled={!!editing?.is_system}
                onChange={(e) => setForm({ ...form, action_code: e.target.value })}
                placeholder="e.g. pay_review"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Label</label>
              <input
                type="text"
                value={form.action_label}
                onChange={(e) => setForm({ ...form, action_label: e.target.value })}
                placeholder="e.g. Pay Review"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Trigger Type</label>
              <select
                value={form.trigger_type}
                onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              >
                {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Default Priority</label>
              <select
                value={form.default_priority}
                onChange={(e) => setForm({ ...form, default_priority: e.target.value })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Due Days</label>
              <input
                type="number"
                value={form.default_due_days}
                onChange={(e) => setForm({ ...form, default_due_days: parseInt(e.target.value) || 30 })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Action URL Template</label>
              <input
                type="text"
                value={form.action_url_template}
                onChange={(e) => setForm({ ...form, action_url_template: e.target.value })}
                placeholder="/admin/employees/{employee_id}?tab=compensation"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 block mb-1">Task Title Template</label>
              <input
                type="text"
                value={form.task_title_template}
                onChange={(e) => setForm({ ...form, task_title_template: e.target.value })}
                placeholder="Pay review for {employee_name}"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 block mb-1">Task Description Template</label>
              <textarea
                rows={2}
                value={form.task_description_template}
                onChange={(e) => setForm({ ...form, task_description_template: e.target.value })}
                placeholder="Description with {employee_name}, {manager_name}, {cycle_name} variables..."
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            {/* The notify_* columns were retired: the resolver ignores them.
                Routing now lives entirely in Settings > Notifications. */}
            <div className="md:col-span-2">
              <ActionRoutingSummary actionCode={form.action_code} />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-gray-300" />
                Active
              </label>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4 border-t border-gray-100 pt-3">
            <button type="button" onClick={() => { setEditing(null); setCreating(false); }} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="button" onClick={() => void save()} disabled={saving || !form.action_code || !form.action_label} className="rounded-lg bg-pme-blue px-3 py-2 text-xs font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50">
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            Template variables: {"{employee_name}"}, {"{employee_id}"}, {"{cycle_name}"}, {"{manager_name}"}
          </p>
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast("")} />}
    </div>
  );
}

function FeatureTogglesTab() {
  const { refresh: refreshFeatures } = useFeatures();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data: Record<string, boolean> = await api.get("/api/reviews/features");
      setFlags(data);
    } catch (err) {
      if (err instanceof ApiError) setErrorMsg(err.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const toggleFeature = async (key: string) => {
    const prev = { ...flags };
    const newValue = !flags[key];
    setFlags({ ...flags, [key]: newValue });
    setUpdatingKey(key);
    setErrorMsg(null);
    try {
      await api.put("/api/reviews/features", { key, enabled: newValue });
      await refreshFeatures();
      const label = FEATURE_META[key]?.label ?? key;
      setToast(`${label} ${newValue ? "enabled" : "disabled"}`);
    } catch (err) {
      setFlags(prev);
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to save");
    } finally { setUpdatingKey(null); }
  };

  if (loading) return <p className="text-sm text-gray-400">Loading features...</p>;

  const featureKeys = Object.keys(FEATURE_META);

  return (
    <div className="space-y-4">
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
      {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
      <p className="text-sm text-gray-500">Enable or disable platform features.</p>
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
        {featureKeys.map((key) => {
          const meta = FEATURE_META[key];
          const enabled = !!flags[key];
          return (
            <div key={key} className="flex items-center justify-between px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                <p className="text-xs text-gray-500">{meta.description}</p>
              </div>
              <button
                type="button"
                disabled={updatingKey === key}
                onClick={() => toggleFeature(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enabled ? "bg-pme-blue" : "bg-gray-300"
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enabled ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 8: System
 * ──────────────────────────────────────────────────────────────────────────── */

interface HealthResponse {
  status: string;
  [key: string]: unknown;
}

function SystemTab() {
  const [healthStatus, setHealthStatus] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);

  const appVersion = (() => {
    const meta = document.querySelector('meta[name="app-version"]');
    return meta?.getAttribute("content") || "1.0.0";
  })();

  const environment = (() => {
    const hostname = window.location.hostname;
    if (hostname === "people.pmecake.co.uk") return "Production";
    if (hostname.includes("people-dev")) return "Development";
    if (hostname === "localhost" || hostname === "127.0.0.1") return "Local";
    return "Unknown";
  })();

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<HealthResponse>("/api/health");
        setHealthStatus(res.status || "ok");
      } catch (err) {
        setHealthError((err as Error).message);
      } finally {
        setHealthLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900">PME People</h3>
          <p className="mt-1 text-sm text-gray-500">
            Time &amp; Attendance and HR Platform
          </p>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <Detail label="App Version" value={appVersion} />
          <Detail label="Environment" value={environment} />
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">
              Database Status
            </p>
            <p className="mt-1 font-medium">
              {healthLoading ? (
                <span className="text-gray-500">Checking...</span>
              ) : healthError ? (
                <span className="text-red-600">Error: {healthError}</span>
              ) : (
                <span
                  className={
                    healthStatus === "ok" || healthStatus === "healthy"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }
                >
                  {healthStatus === "ok" || healthStatus === "healthy"
                    ? "Connected"
                    : healthStatus}
                </span>
              )}
            </p>
          </div>
        </dl>

        <div className="mt-8 border-t border-gray-200 pt-4 text-center">
          <p className="text-xs text-gray-400">Powered by PME Cake</p>
        </div>
      </div>
    </div>
  );
}

interface PendingApproval {
  id: number;
  workflow_name: string | null;
  request_type: string;
  requested_by_name: string | null;
  created_at: string;
  status: string;
}

function ApprovalsTab() {
  const [workflows, setWorkflows] = useState<{ id: number; name: string; workflow_type: string; description: string | null; is_active: boolean; steps: { id: number; step_order: number; approver_type: string; can_skip: boolean }[] }[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [decidingId, setDecidingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setWorkflows(await api.get("/api/approvals/workflows")); } catch {}
  }, []);
  const loadPending = useCallback(async () => {
    try { setPendingApprovals(await api.get("/api/approvals/pending")); } catch {}
  }, []);
  useEffect(() => { void load(); void loadPending(); }, [load, loadPending]);

  const handleApprove = async (requestId: number) => {
    setDecidingId(requestId);
    try {
      await api.post(`/api/approvals/${requestId}/decide`, { decision: "approved" });
      void loadPending();
    } catch {} finally { setDecidingId(null); }
  };

  const handleReject = async (requestId: number) => {
    const comment = window.prompt("Rejection reason (optional):");
    if (comment === null) return;
    setDecidingId(requestId);
    try {
      await api.post(`/api/approvals/${requestId}/decide`, { decision: "rejected", comment: comment || null });
      void loadPending();
    } catch {} finally { setDecidingId(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/api/approvals/workflows", { name: formName, workflow_type: formType, description: formDesc || null });
      setShowForm(false); setFormName(""); setFormType(""); setFormDesc("");
      void load();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* ── Pending Approvals ── */}
      {pendingApprovals.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Pending Approvals</h3>
          {pendingApprovals.map((pa) => (
            <div key={pa.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{pa.workflow_name || "—"}</p>
                <p className="text-xs text-gray-500">
                  {pa.request_type} {pa.requested_by_name ? `by ${pa.requested_by_name}` : ""}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {new Date(pa.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleApprove(pa.id)}
                  disabled={decidingId === pa.id}
                  className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                >
                  {decidingId === pa.id ? "..." : "Approve"}
                </button>
                <button
                  type="button"
                  onClick={() => handleReject(pa.id)}
                  disabled={decidingId === pa.id}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Configure approval chains for leave, expenses, and other requests.</p>
        <button type="button" onClick={() => setShowForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add Workflow</button>
      </div>
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Name</span>
            <input value={formName} onChange={(e) => setFormName(e.target.value)} className="input mt-1" placeholder="e.g. Leave Approval" />
          </label>
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Type</span>
            <input value={formType} onChange={(e) => setFormType(e.target.value)} className="input mt-1" placeholder="e.g. leave_request" />
          </label>
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Description</span>
            <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} rows={2} className="input mt-1" />
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={saving || !formName || !formType} onClick={save} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
      {workflows.map((w) => (
        <div key={w.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{w.name}</p>
              <p className="text-xs text-gray-500">{w.workflow_type} {w.is_active ? "" : "(inactive)"}</p>
              {w.description && <p className="mt-1 text-xs text-gray-600">{w.description}</p>}
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${w.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {w.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          {w.steps.length > 0 && (
            <div className="mt-3 space-y-1">
              {w.steps.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-pme-blue/10 text-[10px] font-bold text-pme-blue">{s.step_order}</span>
                  <span>{s.approver_type}{s.can_skip ? " (skippable)" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {workflows.length === 0 && !showForm && <p className="text-sm text-gray-400">No approval workflows configured.</p>}
    </div>
  );
}

function SurveysTab() {
  const [configs, setConfigs] = useState<{ id: number; survey_type: string; name: string; frequency: string; is_active: boolean; target_audience: string }[]>([]);
  const [results, setResults] = useState<{ config: { id: number; name: string }; response_count: number; enps: number | null; avg_happiness: number | null; avg_motivation: number | null } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("enps");
  const [formFreq, setFormFreq] = useState("monthly");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setConfigs(await api.get("/api/surveys/configs")); } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/api/surveys/configs", { survey_type: formType, name: formName, frequency: formFreq });
      setShowForm(false); setFormName(""); void load();
    } catch {} finally { setSaving(false); }
  };

  const viewResults = async (id: number) => {
    try { setResults(await api.get(`/api/surveys/${id}/results`)); } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Configure employee satisfaction (eNPS) and wellbeing surveys.</p>
        <button type="button" onClick={() => setShowForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add Survey</button>
      </div>
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Name</span>
            <input value={formName} onChange={(e) => setFormName(e.target.value)} className="input mt-1" placeholder="e.g. Monthly eNPS" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Type</span>
              <select value={formType} onChange={(e) => setFormType(e.target.value)} className="input mt-1">
                <option value="enps">eNPS</option>
                <option value="wellbeing">Wellbeing</option>
                <option value="pulse">Pulse</option>
              </select>
            </label>
            <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Frequency</span>
              <select value={formFreq} onChange={(e) => setFormFreq(e.target.value)} className="input mt-1">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" disabled={saving || !formName} onClick={save} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
      {results && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">{results.config.name} — Results</h3>
            <button type="button" onClick={() => setResults(null)} className="text-xs text-gray-500">Close</button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div><p className="text-xs text-gray-500">Responses</p><p className="text-lg font-bold">{results.response_count}</p></div>
            {results.enps != null && <div><p className="text-xs text-gray-500">eNPS</p><p className="text-lg font-bold">{results.enps}</p></div>}
            {results.avg_happiness != null && <div><p className="text-xs text-gray-500">Happiness</p><p className="text-lg font-bold">{results.avg_happiness}/5</p></div>}
            {results.avg_motivation != null && <div><p className="text-xs text-gray-500">Motivation</p><p className="text-lg font-bold">{results.avg_motivation}/5</p></div>}
          </div>
        </div>
      )}
      {configs.map((c) => (
        <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{c.name}</p>
              <p className="text-xs text-gray-500">{c.survey_type} · {c.frequency} · {c.target_audience}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => viewResults(c.id)} className="rounded border border-gray-300 px-2 py-1 text-xs">Results</button>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                {c.is_active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      ))}
      {configs.length === 0 && !showForm && <p className="text-sm text-gray-400">No surveys configured.</p>}
    </div>
  );
}

function CalendarFeedsTab() {
  const [tokens, setTokens] = useState<{ id: number; token: string; feed_type: string; is_active: boolean; created_at: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [feedType, setFeedType] = useState("leave");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setTokens(await api.get("/api/calendar/tokens")); } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/api/calendar/tokens", { feed_type: feedType });
      setShowForm(false); void load();
    } catch {} finally { setSaving(false); }
  };

  const revoke = async (id: number) => {
    try { await api.delete(`/api/calendar/tokens/${id}`); void load(); } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Generate iCalendar feed URLs for external calendar subscriptions.</p>
        <button type="button" onClick={() => setShowForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ New Feed</button>
      </div>
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Feed Type</span>
            <select value={feedType} onChange={(e) => setFeedType(e.target.value)} className="input mt-1">
              <option value="leave">Leave</option>
              <option value="events">Company Events</option>
              <option value="holidays">Public Holidays</option>
              <option value="all">All</option>
            </select>
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={create} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Creating..." : "Create Feed"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
      {tokens.map((t) => (
        <div key={t.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">{t.feed_type} feed</p>
              <p className="mt-1 truncate text-xs font-mono text-gray-500">{window.location.origin}/api/calendar/feed/{t.token}.ics</p>
              <p className="text-xs text-gray-400">Created: {new Date(t.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { void navigator.clipboard.writeText(`${window.location.origin}/api/calendar/feed/${t.token}.ics`); }} className="rounded border border-gray-300 px-2 py-1 text-xs">Copy URL</button>
              {t.is_active && <button type="button" onClick={() => revoke(t.id)} className="rounded border border-red-300 px-2 py-1 text-xs text-red-600">Revoke</button>}
              {!t.is_active && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">Revoked</span>}
            </div>
          </div>
        </div>
      ))}
      {tokens.length === 0 && !showForm && <p className="text-sm text-gray-400">No calendar feeds created.</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tab 13b: Calendar Colours
 * ──────────────────────────────────────────────────────────────────────────── */

interface CalendarColourData {
  sites: { id: number; name: string; colour: string | null }[];
  event_type_colours: Record<string, string>;
  event_type_text_colours: Record<string, string>;
  site_text_colours: Record<string, string>;
}

function autoTextSuggestion(bg: string): string {
  const h = bg.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const toL = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lum = 0.2126 * toL(r) + 0.7152 * toL(g) + 0.0722 * toL(b);
  return lum < 0.5 ? "#FFFFFF" : "#000000";
}

function CalendarColoursTab() {
  const [data, setData] = useState<CalendarColourData | null>(null);
  const [siteColours, setSiteColours] = useState<Record<string, string>>({});
  const [siteTextColours, setSiteTextColours] = useState<Record<string, string>>({});
  const [eventColours, setEventColours] = useState<Record<string, string>>({});
  const [eventTextColours, setEventTextColours] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.get<CalendarColourData>("/api/settings/calendar-colours");
      setData(d);
      const sc: Record<string, string> = {};
      for (const s of d.sites) {
        if (s.colour) sc[String(s.id)] = s.colour;
      }
      setSiteColours(sc);
      setSiteTextColours(d.site_text_colours || {});
      setEventColours(d.event_type_colours || {});
      setEventTextColours(d.event_type_text_colours || {});
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/api/settings/calendar-colours", {
        site_colours: siteColours,
        site_text_colours: siteTextColours,
        event_type_colours: eventColours,
        event_type_text_colours: eventTextColours,
      });
      setToast("Saved");
      setTimeout(() => setToast(""), 3000);
    } catch {} finally { setSaving(false); }
  };

  if (!data) return <p className="py-4 text-sm text-gray-400">Loading...</p>;

  const EVENT_TYPES = [
    { key: "holiday", label: "Public Holidays" },
    { key: "birthday", label: "Birthdays" },
    { key: "anniversary", label: "Work Anniversaries" },
    { key: "event", label: "Company Events" },
  ];

  return (
    <div className="space-y-6">
      {toast && (
        <div className="rounded-lg bg-green-50 px-4 py-2 text-sm font-medium text-green-700">{toast}</div>
      )}

      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Site Colours</h3>
        <p className="mb-3 text-xs text-gray-500">Assign background and text colours to each site for calendar display.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {data.sites.map((s) => {
            const bg = siteColours[String(s.id)] || "#6B7280";
            const tc = siteTextColours[String(s.id)] || autoTextSuggestion(bg);
            return (
              <div key={s.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <span className="mb-2 block text-sm font-medium text-gray-700">{s.name}</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    Bg
                    <input
                      type="color"
                      value={bg}
                      onChange={(e) => {
                        setSiteColours((prev) => ({ ...prev, [String(s.id)]: e.target.value }));
                        if (!siteTextColours[String(s.id)]) {
                          setSiteTextColours((prev) => ({ ...prev, [String(s.id)]: autoTextSuggestion(e.target.value) }));
                        }
                      }}
                      className="h-7 w-7 cursor-pointer rounded border-0"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    Text
                    <input
                      type="color"
                      value={tc}
                      onChange={(e) => setSiteTextColours((prev) => ({ ...prev, [String(s.id)]: e.target.value }))}
                      className="h-7 w-7 cursor-pointer rounded border-0"
                    />
                  </label>
                  <span
                    className="rounded px-2 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: bg, color: tc }}
                  >
                    Preview
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Event Type Colours</h3>
        <p className="mb-3 text-xs text-gray-500">Set background and text colours for calendar event types.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {EVENT_TYPES.map((et) => {
            const bg = eventColours[et.key] || "#6B7280";
            const tc = eventTextColours[et.key] || autoTextSuggestion(bg);
            return (
              <div key={et.key} className="rounded-lg border border-gray-200 bg-white p-3">
                <span className="mb-2 block text-sm font-medium text-gray-700">{et.label}</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    Bg
                    <input
                      type="color"
                      value={bg}
                      onChange={(e) => {
                        setEventColours((prev) => ({ ...prev, [et.key]: e.target.value }));
                        if (!eventTextColours[et.key]) {
                          setEventTextColours((prev) => ({ ...prev, [et.key]: autoTextSuggestion(e.target.value) }));
                        }
                      }}
                      className="h-7 w-7 cursor-pointer rounded border-0"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    Text
                    <input
                      type="color"
                      value={tc}
                      onChange={(e) => setEventTextColours((prev) => ({ ...prev, [et.key]: e.target.value }))}
                      className="h-7 w-7 cursor-pointer rounded border-0"
                    />
                  </label>
                  <span
                    className="rounded px-2 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: bg, color: tc }}
                  >
                    Preview
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="rounded-lg bg-pme-blue px-6 py-2 text-sm font-semibold text-white disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save Colours"}
      </button>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────────
 * Tab 14: Recognition (company values management)
 * ──────────────────────────────────────────────────────────────────────────── */

function RecognitionValuesTab() {
  const [values, setValues] = useState<CompanyValue[]>([]);
  const [suggested, setSuggested] = useState<CompanyValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formIcon, setFormIcon] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, pending] = await Promise.all([
        api.get<CompanyValue[]>("/api/recognition/values?include_unapproved=true"),
        api.get<CompanyValue[]>("/api/recognition/values/suggested"),
      ]);
      setValues(all);
      setSuggested(pending);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const resetForm = () => {
    setFormName(""); setFormDesc(""); setFormIcon("");
    setEditingId(null); setShowForm(false);
  };

  const startEdit = (v: CompanyValue) => {
    setFormName(v.name); setFormDesc(v.description ?? ""); setFormIcon(v.icon ?? "");
    setEditingId(v.id); setShowForm(true);
  };

  const saveValue = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/recognition/values/${editingId}`, { name: formName, description: formDesc || null, icon: formIcon || null });
        setNotice("Value updated.");
      } else {
        await api.post("/api/recognition/values", { name: formName, description: formDesc || null, icon: formIcon || null });
        setNotice("Value created.");
      }
      resetForm();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id: number) => {
    try {
      await api.post(`/api/recognition/values/${id}/approve`, {});
      setNotice("Value approved.");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed");
    }
  };

  const reject = async (id: number) => {
    try {
      await api.post(`/api/recognition/values/${id}/reject`, {});
      setNotice("Value rejected.");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed");
    }
  };

  if (loading) return <p className="text-sm text-gray-400">Loading...</p>;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}
          <button type="button" onClick={() => setError(null)} className="ml-2 font-semibold">Dismiss</button>
        </div>
      )}
      {notice && (
        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}
          <button type="button" onClick={() => setNotice(null)} className="ml-2 font-semibold">Dismiss</button>
        </div>
      )}

      {/* Pending suggestions */}
      {suggested.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-amber-700">Pending Suggestions ({suggested.length})</h3>
          <div className="space-y-2">
            {suggested.map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{v.name}</p>
                  {v.suggested_by_name && <p className="text-xs text-gray-500">Suggested by {v.suggested_by_name}</p>}
                  {v.description && <p className="text-xs text-gray-500">{v.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => void approve(v.id)} className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700">Approve</button>
                  <button type="button" onClick={() => void reject(v.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">Reject</button>
                  <button type="button" onClick={() => startEdit(v)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">Edit</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All values */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Company Values</h3>
          <button type="button" onClick={() => { resetForm(); setShowForm(true); }} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">Add Value</button>
        </div>

        {showForm && (
          <div className="mb-4 space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Name *</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Description</label>
                <input type="text" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => void saveValue()} disabled={saving || !formName.trim()} className="rounded-lg bg-pme-blue px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Saving..." : editingId ? "Update" : "Create"}</button>
              <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {values.filter((v) => v.is_approved).map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <div>
                <p className="text-sm font-semibold text-gray-900">{v.name}</p>
                {v.description && <p className="text-xs text-gray-500">{v.description}</p>}
              </div>
              <button type="button" onClick={() => startEdit(v)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">Edit</button>
            </div>
          ))}
          {values.filter((v) => v.is_approved).length === 0 && (
            <p className="text-sm text-gray-400">No company values configured.</p>
          )}
        </div>
      </div>
    </div>
  );
}
